<script>
	function passwordVisibility(node) {
		return {
			update(isVisible) {
				node.type = isVisible ? 'text' : 'password';
			}
		};
	}
	import { onMount } from 'svelte';

	// State
	let isAuthenticated = false;
	let currentView = 'login';
	let user = { email: '', password: '' };
	let isAdmin = false;
	let alert = { show: false, message: '', type: '' };
	let loading = false;
	let isRefreshing = false;
	let activeLoginTab = 'login'; // For login/signup tabs
	let currentUser = null; // Store the currently logged in user details
	let showPassword = false; // For password visibility toggle
	let showConfirmPassword = false; // For confirm password visibility toggle


	// Forgot Password functionality
	let forgotPasswordEmail = '';
	let showForgotPasswordModal = false;
	let forgotPasswordNewPassword = '';
	let forgotPasswordConfirmPassword = '';
	let showForgotPasswordNew = false; // For password visibility toggle
	let showForgotPasswordConfirm = false; // For confirm password visibility toggle

	// Password reset state
	let resetPasswordState = 'loading'; // loading, invalid, valid, success
	let resetPasswordData = {
		token: '',
		password: '', // Note: Password fields are only used in the reset-password view after clicking the email link
		confirmPassword: '' // Note: Password fields are only used in the reset-password view after clicking the email link
	};

	// Business registration form
	let businessRegistration = {
		owner_name: '',
		business_name: '',
		owner_email: '',
		owner_cell: '',
		password: '',
		confirmPassword: ''
	};

	// Customer points history
	let pointsHistory = [];

	// Email settings (kept in code but removed from UI)
	// Note: These settings are not exposed in the UI in the compiled build
	let emailSettings = {
		provider: 'smtp', // smtp or api
		smtp: {
			host: '',
			port: 587,
			username: '',
			password: '',
			secure: false
		},
		api: {
			url: '',
			apiKey: '',
			fromEmail: '',
			fromName: 'Loyalty Pro'
		}
	};

	// Message templates for admin settings
	// Note: These templates are not exposed in the UI in the compiled build
	let messageTemplate = {
		pointEarned: 'Thank you for your purchase at {business_name}! You\'ve earned 1 point. You now have {points} points.',
		rewardAvailable: 'Congratulations! You\'ve earned a freebie! Visit {business_name} to redeem it.',
		pointAwardedAfterRedemption: 'Thanks for redeeming your {product} at {business_name}. You now have {points} points available.'
	};

	let blastMessage = '';
	let emailConfirmModalVisible = false;
	let eligibleEmailCount = 0;

	// Products/Services
	let products = [
		{ id: '1', name: 'Cup of Coffee', points_needed: 10 }
	];
	let newProduct = { name: '', points_needed: 10 };

	// Admin UI state
	let expandedSection = 'products'; // Default expanded section

	// Redemption
	let showRedeemModal = false;
	let selectedService = null;

	// Customer Management
	let customers = [];
	let newCustomer = {
		name: '',
		email: '',
		cell: '',
		points: 0,
		has_reward: false, // Added has_reward based on compiled code structure
		preferred_contact: JSON.stringify({ email: true, cell: false })
	};
	let preferredContactOptions = { email: true, cell: false }; // Default for new customer form
	let selectedCustomer = null;
	let showCustomerModal = false;
	let customerModalMode = 'add'; // add, edit, view

	// Search functionality
	let searchQuery = '';

	// Employee Management
	let employees = [];
	let newEmployee = {
		name: '',
		email: '',
		password: '',
		permissions: {
			view_customers: false,
			add_customers: false,
			delete_customers: false,
			points_history: false,
			marketing_messages: false,
			full_access: false
		}
	};
	let selectedEmployee = null;
	let showEmployeeModal = false;
	let employeeModalMode = 'add'; // add, edit, view

	// Store Branding (Premium Feature)
	let storeBranding = {
		companyName: '',
		logoUrl: '',
		primaryColor: '#30d5c8',
		secondaryColor: '#fc9403',
		useCustomBranding: false
	};
	let logoFile = null;
	let isUploadingLogo = false;
	let showColorPickerModal = false;
	let colorPickerTarget = 'primary'; // 'primary' or 'secondary'

	// Payment info
	// Note: Keeping the more detailed structure from source, but checkPaymentStatus logic matches compiled
	let paymentInfo = {
		businessCreatedAt: null,
		lastPaymentDate: null,
		subscriptionStart: null,   // First billing date from server (signup + 2 months)
		selectedTier: 'basic',
		status: 'free',
		trialEndDate: null,
		nextBillingDate: null,
		amountDue: 49
	};

	const API_URL = window.location.hostname === 'localhost'
		? 'http://localhost/api'
		: '/api';

	// Marketing Site URL
	const MARKETING_SITE_URL = window.location.hostname === 'localhost'
		? 'http://localhost:5173' // Adjust if your marketing site runs on a different port locally
		: 'https://get.loyaltypro.io'; // Your live marketing site URL

	// Helper functions
	function obfuscateName(name) {
		if (!name) return '';
		const parts = name.trim().split(/\s+/);
		if (parts.length < 2) return name;
		const firstName = parts[0];
		const lastName = parts[1];
		// Obfuscate second name part after first 2 characters (matches compiled)
		const repeatCount = Math.max(0, lastName.length - 2);
		return `${firstName} ${lastName.substring(0, 2)}${'*'.repeat(repeatCount)}`;
	}

	function obfuscateCell(cell) {
		if (!cell) return '';
		if (cell.length <= 8) return cell;
		// Obfuscate 4 characters in the middle (matches compiled)
		return `${cell.substring(0, 3)}${'*'.repeat(4)}${cell.substring(cell.length - 3)}`;
	}

	function filterCustomers(customers, query) {
		if (!query) return customers;
		const lowerQuery = query.toLowerCase();
		return customers.filter(customer =>
			customer.name.toLowerCase().includes(lowerQuery) ||
			(customer.email && customer.email.toLowerCase().includes(lowerQuery)) ||
			(customer.cell && customer.cell.toLowerCase().includes(lowerQuery))
		);
	}

	function parsePreferredContact(customer) {
		if (!customer.preferred_contact) {
			return { email: true, cell: false };
		}
		try {
			let parsed = customer.preferred_contact;
			if (typeof customer.preferred_contact === 'string') {
				parsed = JSON.parse(customer.preferred_contact);
			}
			return {
				email: parsed.email || false,
				cell: parsed.cell || false,
			};
		} catch (e) {
			console.error('Error parsing preferred_contact:', e, customer.preferred_contact);
			return { email: true, cell: false };
		}
	}

	function stringifyPreferredContact(preferredContact) {
		const contactToStore = {
			email: preferredContact.email || false,
			cell: preferredContact.cell || false,
		};
		return JSON.stringify(contactToStore);
	}

	function checkPaymentStatus() {
		// Prioritize currentUser if available (after login/register), then localStorage (onMount)
		const businessCreatedAtStr = currentUser?.business_created_at || paymentInfo.businessCreatedAt || localStorage.getItem('businessCreatedAt');
		const lastPaymentDateStr = paymentInfo.lastPaymentDate || localStorage.getItem('lastPaymentDate');

		// Use server-supplied subscription_start (first billing date) if available
		// This is signup_date + 2 months, stored in DB, never changes
		const subscriptionStartStr = currentUser?.subscription_start 
			|| paymentInfo.subscriptionStart 
			|| localStorage.getItem('subscriptionStart');

		// Determine current tier from currentUser (most reliable) or localStorage
		const currentTier = (currentUser?.package?.toLowerCase()) ||
			(localStorage.getItem('subscriptionTier')) ||
			'basic';

		paymentInfo.selectedTier = currentTier;
		paymentInfo.amountDue = currentTier === 'premium' ? 159 : 49;

		if (!businessCreatedAtStr) {
			console.warn("Business creation date not found. Cannot accurately determine payment status.");
			paymentInfo.status = 'unknown';
			paymentInfo.trialEndDate = null;
			paymentInfo.nextBillingDate = null;
			return;
		}

		// Prefer server-supplied subscription_start (first billing date)
		// Fall back to calculating from created_at if not available
		let trialEndDate;
		if (subscriptionStartStr) {
			trialEndDate = new Date(subscriptionStartStr);
			if (isNaN(trialEndDate.getTime())) {
				// Invalid subscription_start, fall back to calculation
				const creationDate = new Date(businessCreatedAtStr);
				trialEndDate = new Date(creationDate);
				trialEndDate.setMonth(trialEndDate.getMonth() + 2);
			}
		} else {
			const creationDate = new Date(businessCreatedAtStr);
			if (isNaN(creationDate.getTime())) {
				console.error("Invalid business creation date:", businessCreatedAtStr);
				paymentInfo.status = 'error';
				paymentInfo.trialEndDate = null;
				paymentInfo.nextBillingDate = null;
				return;
			}
			trialEndDate = new Date(creationDate);
			trialEndDate.setMonth(trialEndDate.getMonth() + 2);
		}

		paymentInfo.trialEndDate = trialEndDate; // Store for display regardless of status

		const now = new Date();

		if (now < trialEndDate) {
			// Still in free trial
			paymentInfo.status = 'free';
			// nextBillingDate = first billing date (= trialEndDate = subscription_start)
			paymentInfo.nextBillingDate = new Date(trialEndDate.getTime());
		} else {
			// Trial has ended
			let potentialNextBillingDate = new Date(trialEndDate.getTime()); // First payment due at trial end

			if (lastPaymentDateStr) {
				const lastPayment = new Date(lastPaymentDateStr);
				if (!isNaN(lastPayment.getTime()) && lastPayment >= trialEndDate) {
					// Valid payment after trial end: next bill = last_payment + 1 month
					potentialNextBillingDate = new Date(lastPayment.getTime());
					potentialNextBillingDate.setMonth(potentialNextBillingDate.getMonth() + 1);
				}
			}

			paymentInfo.nextBillingDate = potentialNextBillingDate;

			if (now >= paymentInfo.nextBillingDate) {
				paymentInfo.status = 'due';
			} else {
				paymentInfo.status = 'paid';
			}
		}
	}

	function formatDate(date) {
		if (!date || isNaN(new Date(date).getTime())) return 'N/A';
		const d = new Date(date);
		return d.toLocaleDateString('en-ZA', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	}

	function formatTime(date) {
		if (!date || isNaN(new Date(date).getTime())) return 'N/A';
		const d = new Date(date);
		return d.toLocaleTimeString('en-ZA', {
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	function handleSearch() { /* Handled by reactive $: */ }

	function toggleSection(section) {
		expandedSection = expandedSection === section ? null : section;
	}

	function getMinPointsForFreebie() {
		if (!products || products.length === 0) return 10;
		const pointsArray = products.map(p => p.points_needed || 10);
		return pointsArray.length > 0 ? Math.min(...pointsArray) : 10;
	}
	function calculateFreebies(points) {
		const minPoints = getMinPointsForFreebie();
		if (minPoints <= 0) return 0; return Math.floor(points / minPoints);
	}
	function calculateProgress(points) {
		const minPoints = getMinPointsForFreebie();
		if (minPoints <= 0) return 0;
		const remainingPoints = points % minPoints;
		return minPoints > 0 ? (remainingPoints / minPoints) * 100 : 0;
	}
	function setupPullToRefresh() { /* ... unchanged ... */ }

	async function refreshData(silent = false) {
		try {
			loading = true;
			await loadCustomers();
			await loadProducts();
			await loadPointsHistory();
			await loadMessageTemplates();
			
			// Try to load additional data gracefully to ensure full freshness
			try { await loadEmployees(); } catch (e) { console.error(e); }
			try { await loadStoreBranding(); } catch (e) { console.error(e); }
			
			checkPaymentStatus();
			paymentInfo = { ...paymentInfo };
			if (silent !== true) {
				showAlert('Data refreshed', 'success');
			}
		} catch (error) {
			if (silent !== true) {
				showAlert('Error refreshing data', 'danger');
			}
		} finally {
			isRefreshing = false;
			loading = false;
		}
	}

	function navigateToPayment() {
		expandedSection = 'payment';
		currentView = 'admin';
	}
	function isValidSmsNumber(cell) { /* ... unchanged ... */ }
	async function countValidEmails() { /* ... unchanged ... */ }

	function openEmailConfirmModal() {
		if (!blastMessage) { showAlert('Please enter a message to send', 'danger'); return; }
		countValidEmails();
		emailConfirmModalVisible = true;
	}
	function closeEmailConfirmModal() { emailConfirmModalVisible = false; }

	async function sendMarketingEmails() {
		try {
			loading = true;
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); closeEmailConfirmModal(); loading = false; return; }
			if (isAdmin) { showAlert(`Sending promotional emails to ${eligibleEmailCount} customers.`, 'success'); closeEmailConfirmModal(); blastMessage = ''; loading = false; return; }

			const response = await fetch(`${API_URL}/notifications/send_blast.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ message: blastMessage })
			});
			const data = await response.json();
			if (data.success) {
				showAlert(`Email sent to ${data.sent_count} customers.`, 'success');
				closeEmailConfirmModal(); blastMessage = '';
			} else {
				showAlert(data.error || (data.message || 'Failed to send emails'), 'danger');
			}
		} catch (error) { showAlert(`Error sending emails: ${error.message}`, 'danger'); }
		finally { loading = false; }
	}

	function exportToCsv() {
		if (!pointsHistory.length) {
			showAlert('No transaction history to export', 'warning');
			return;
		}

		const headers = ['Customer', 'Action', 'Redeemed', 'Balance', 'Lifetime Points', 'Lifetime Freebies', 'Date', 'Time'];
		const rows = pointsHistory.map(entry => [
			entry.customer_name,
			entry.action,
			entry.redeemed_item || '',
			`${entry.point_balance} Points`,
			`${entry.lifetime_points || 0} Points`,
			`${entry.lifetime_redemptions || 0}`,
			formatDate(entry.created_at),
			formatTime(entry.created_at)
		]);

		let csvContent = headers.join(',') + '\n';
		rows.forEach(row => {
			const formattedRow = row.map(cell => {
				// Escape quotes and wrap in quotes if contains comma
				const formattedCell = String(cell).replace(/"/g, '""');
				return formattedCell.includes(',') ? `"${formattedCell}"` : formattedCell;
			});
			csvContent += formattedRow.join(',') + '\n';
		});

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `points_history_${formatDate(new Date())}.csv`);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	async function emailPointsSummary() {
		if (!customers.length) {
			showAlert('No customers to include in summary', 'warning');
			return;
		}

		if (paymentInfo.selectedTier !== 'premium') {
			showAlert('This feature is only available for Premium subscribers', 'warning');
			return;
		}

		try {
			loading = true;
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				showAlert('You must be logged in.', 'danger');
				return;
			}

			if (isAdmin) {
				showAlert('Points summary email sent to your email address (demo mode)', 'success');
				return;
			}

			const response = await fetch(`${API_URL}/history/email_summary.php`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				}
			});

			const data = await response.json();
			if (data.success) {
				showAlert(data.message || 'Points summary email sent successfully', 'success');
			} else {
				showAlert(data.error || 'Failed to send points summary email', 'danger');
			}
		} catch (error) {
			showAlert(`Error sending points summary: ${error.message}`, 'danger');
		} finally {
			loading = false;
		}
	}

	// Computed Values
	$: filteredCustomers = filterCustomers(customers, searchQuery);
	$: minPointsNeeded = getMinPointsForFreebie();
	$: newCustomer.preferred_contact = stringifyPreferredContact(preferredContactOptions);

	// Auth Methods
	async function login() {
		if (!user.email || !user.password) { showAlert('Email and password are required', 'danger'); return; }
		try {
			loading = true;
			if (user.email === 'admin@loyaltypro.io' && user.password === 'Password') {
				isAuthenticated = true; isAdmin = true; currentView = 'customers';
				showAlert('Logged in successfully as admin', 'success');
				localStorage.setItem('isAuthenticated', 'true'); localStorage.setItem('isAdmin', 'true');
				loading = false; return;
			}
			const response = await fetch(`${API_URL}/auth/login.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', },
				body: JSON.stringify({ email: user.email, password: user.password })
			});
			const data = await response.json();
			if (data.success && data.user) {
				localStorage.setItem('token', data.token);
				localStorage.setItem('userData', JSON.stringify(data.user));
				localStorage.setItem('isAuthenticated', 'true');
				if (data.user.business_created_at) {
					localStorage.setItem('businessCreatedAt', data.user.business_created_at);
				}
				if (data.user.subscription_start) {
					localStorage.setItem('subscriptionStart', data.user.subscription_start);
				}
				isAuthenticated = true; currentUser = data.user;
				if (currentUser.package) {
					const normPackage = currentUser.package.toLowerCase();
					paymentInfo.selectedTier = normPackage;
					localStorage.setItem('subscriptionTier', normPackage);
				} else {
					paymentInfo.selectedTier = 'basic'; localStorage.setItem('subscriptionTier', 'basic');
				}
				// Load data with error handling to prevent breaking the app
				try { await loadProducts(); } catch (e) { console.error('Failed to load products:', e); products = []; }
				try { await loadCustomers(); } catch (e) { console.error('Failed to load customers:', e); customers = []; }
				try { await loadPointsHistory(); } catch (e) { console.error('Failed to load history:', e); pointsHistory = []; }
				try { await loadMessageTemplates(); } catch (e) { console.error('Failed to load templates:', e); messageTemplates = {}; }
				try { await loadEmployees(); } catch (e) { console.error('Failed to load employees:', e); employees = []; }
				try { await loadStoreBranding(); } catch (e) { console.error('Failed to load store branding:', e); }

				paymentInfo.businessCreatedAt = currentUser.business_created_at;
				paymentInfo.subscriptionStart = currentUser.subscription_start || null;
				paymentInfo.lastPaymentDate = localStorage.getItem('lastPaymentDate');
				checkPaymentStatus();
				paymentInfo = { ...paymentInfo };

				currentView = 'customers';
				showAlert('Logged in successfully', 'success');
			} else { showAlert(data.error || 'Invalid email or password', 'danger'); }
		} catch (error) { showAlert(`Login failed: ${error.message}`, 'danger'); }
		finally { loading = false; }
	}

	function togglePasswordVisibility() { showPassword = !showPassword; }
	function toggleConfirmPasswordVisibility() { showConfirmPassword = !showConfirmPassword; }
	function openForgotPasswordModal() {
		forgotPasswordEmail = user.email || '';
		forgotPasswordNewPassword = '';
		forgotPasswordConfirmPassword = '';
		showForgotPasswordModal = true;
	}
	function closeForgotPasswordModal() {
		showForgotPasswordModal = false;
		forgotPasswordNewPassword = '';
		forgotPasswordConfirmPassword = '';
	}
	async function sendPasswordResetLink() {
		if (!forgotPasswordEmail) { showAlert('Please enter your email address', 'danger'); return; }
		if (!forgotPasswordNewPassword) { showAlert('Please enter your new password', 'danger'); return; }
		if (forgotPasswordNewPassword.length < 8) { showAlert('Password must be at least 8 characters long', 'danger'); return; }
		if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) { showAlert('Passwords do not match', 'danger'); return; }
		try {
			loading = true;
			const response = await fetch(`${API_URL}/auth/reset-password.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', },
				body: JSON.stringify({ email: forgotPasswordEmail, password: forgotPasswordNewPassword })
			});
			const data = await response.json();
			if (data.success) {
				showAlert('Password reset link sent to your email. Click the link to confirm the password change.', 'success');
				closeForgotPasswordModal();
			} else { showAlert(data.error || 'Failed to send reset link', 'danger'); }
		} catch (error) { showAlert(`Error sending reset link: ${error.message}`, 'danger'); }
		finally { loading = false; }
	}

	async function registerBusiness() {
		if (businessRegistration.password !== businessRegistration.confirmPassword) { showAlert('Passwords do not match', 'danger'); return; }
		if (!businessRegistration.owner_name || !businessRegistration.business_name ||
			!businessRegistration.owner_email || !businessRegistration.password) { showAlert('Please fill all required fields', 'danger'); return; }
		try {
			loading = true;
			const response = await fetch(`${API_URL}/auth/register.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', },
				body: JSON.stringify({
					ownerName: businessRegistration.owner_name,
					businessName: businessRegistration.business_name,
					ownerEmail: businessRegistration.owner_email,
					ownerCell: businessRegistration.owner_cell,
					password: businessRegistration.password
				})
			});
			const data = await response.json();
			if (data.success && data.user) {
				localStorage.setItem('token', data.token);
				localStorage.setItem('userData', JSON.stringify(data.user));
				localStorage.setItem('isAuthenticated', 'true');
				if (data.user.business_created_at) {
					localStorage.setItem('businessCreatedAt', data.user.business_created_at);
				}
				if (data.user.subscription_start) {
					localStorage.setItem('subscriptionStart', data.user.subscription_start);
				}
				isAuthenticated = true; currentUser = data.user;
				if (currentUser.package) {
					const normPackage = currentUser.package.toLowerCase();
					paymentInfo.selectedTier = normPackage;
					localStorage.setItem('subscriptionTier', normPackage);
				} else {
					paymentInfo.selectedTier = 'basic'; localStorage.setItem('subscriptionTier', 'basic');
				}
				// Load data with error handling to prevent breaking the app
				try { await loadProducts(); } catch (e) { console.error('Failed to load products:', e); products = []; }
				try { await loadCustomers(); } catch (e) { console.error('Failed to load customers:', e); customers = []; }
				try { await loadPointsHistory(); } catch (e) { console.error('Failed to load history:', e); pointsHistory = []; }
				try { await loadMessageTemplates(); } catch (e) { console.error('Failed to load templates:', e); messageTemplates = {}; }
				try { await loadStoreBranding(); } catch (e) { console.error('Failed to load store branding:', e); }

				paymentInfo.businessCreatedAt = currentUser.business_created_at;
				paymentInfo.subscriptionStart = currentUser.subscription_start || null;
				paymentInfo.lastPaymentDate = null;
				checkPaymentStatus();
				paymentInfo = { ...paymentInfo };

				currentView = 'customers';
				showAlert('Business registered successfully', 'success');
				businessRegistration = { owner_name: '', business_name: '', owner_email: '', owner_cell: '', password: '', confirmPassword: '' };
			} else { showAlert(data.error || 'Registration failed', 'danger'); }
		} catch (error) { showAlert(`Registration error: ${error.message}`, 'danger'); }
		finally { loading = false; }
	}

	function logout() {
		// Aggressively clear all local and session storage
		localStorage.clear();
		sessionStorage.clear();

		isAuthenticated = false; isAdmin = false; currentView = 'login'; currentUser = null;
		user = { email: '', password: '' };

		// Clear local state arrays immediately
		customers = [];
		products = [];
		pointsHistory = [];
		employees = [];

		paymentInfo = {
			businessCreatedAt: null, lastPaymentDate: null, selectedTier: 'basic',
			status: 'free', trialEndDate: null, nextBillingDate: null, amountDue: 49
		};
		
		showAlert('Logged out successfully', 'success');
		setTimeout(() => { window.location.reload(true); }, 500);
	}

	async function updateSubscriptionPlan(tier) {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) return { success: false, error: 'Not authenticated' };
			if (isAdmin) return { success: true, newPackage: tier.charAt(0).toUpperCase() + tier.slice(1) };

			const response = await fetch(`${API_URL}/business/change_plan.php`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ package: tier.charAt(0).toUpperCase() + tier.slice(1) })
			});

			const data = await response.json();
			console.log("Plan update response from API:", data);

			if (data.success && data.package) {
				return { success: true, newPackage: data.package };
			} else {
				return { success: false, error: data.error || 'Failed to update plan on server' };
			}
		} catch (error) {
			console.error("Error updating subscription plan:", error);
			return { success: false, error: error.message };
		}
	}

	async function selectSubscriptionTier(newTier) {
		const currentActualPlan = currentUser?.package?.toLowerCase();

		if (currentActualPlan === newTier) {
			showAlert(`You are already on the ${newTier.charAt(0).toUpperCase() + newTier.slice(1)} plan.`, 'info');
			return;
		}

		if (currentUser && currentUser.business_created_at) {
			paymentInfo.businessCreatedAt = currentUser.business_created_at;
		} else if (localStorage.getItem('businessCreatedAt')) {
			paymentInfo.businessCreatedAt = localStorage.getItem('businessCreatedAt');
		}
		paymentInfo.lastPaymentDate = localStorage.getItem('lastPaymentDate');

		const originalDisplayTier = paymentInfo.selectedTier;
		paymentInfo.selectedTier = currentActualPlan || 'basic';
		checkPaymentStatus();
		const currentNextBillingDate = paymentInfo.nextBillingDate;
		paymentInfo.selectedTier = originalDisplayTier;

		let confirmationMessage = "";
		let proceedWithChange = false;

		if (newTier === 'basic' && currentActualPlan === 'premium') {
			confirmationMessage =
				`You are currently on the Premium plan, active until ${formatDate(currentNextBillingDate)}.\n\n` +
				`If you downgrade now:\n` +
				`- You will continue to enjoy Premium features until ${formatDate(currentNextBillingDate)}.\n` +
				`- Your plan will switch to Basic on ${formatDate(currentNextBillingDate)}, and your next bill will be R49.\n\n` +
				`Do you wish to proceed with the downgrade?`;
			if (confirm(confirmationMessage)) proceedWithChange = true;
		} else if (newTier === 'premium' && currentActualPlan === 'basic') {
			confirmationMessage =
				`You are upgrading to the Premium plan.\n\n` +
				`- Premium features will be available immediately.\n` +
				`- Your next bill on ${formatDate(currentNextBillingDate)} will be R159.\n\n` +
				`Do you wish to upgrade?`;
			if (confirm(confirmationMessage)) proceedWithChange = true;
		} else {
			proceedWithChange = true;
		}

		if (proceedWithChange) {
			showAlert(`Processing your change to the ${newTier.charAt(0).toUpperCase() + newTier.slice(1)} plan...`, 'info');
			loading = true;

			const updateResult = await updateSubscriptionPlan(newTier);
			loading = false;

			if (updateResult.success && updateResult.newPackage) {
				currentUser.package = updateResult.newPackage;
				currentUser = { ...currentUser };

				paymentInfo.selectedTier = updateResult.newPackage.toLowerCase();

				localStorage.setItem('userData', JSON.stringify(currentUser));
				localStorage.setItem('subscriptionTier', paymentInfo.selectedTier);

				paymentInfo.businessCreatedAt = currentUser.business_created_at || localStorage.getItem('businessCreatedAt');
				paymentInfo.lastPaymentDate = localStorage.getItem('lastPaymentDate');
				checkPaymentStatus();
				paymentInfo = { ...paymentInfo };

				showAlert(`Your plan has been successfully set to ${updateResult.newPackage}.`, 'success');
			} else {
				showAlert(updateResult.error || `Failed to change plan. Please try again.`, 'danger');
				paymentInfo.selectedTier = currentActualPlan || 'basic';
				localStorage.setItem('subscriptionTier', currentActualPlan || 'basic');
				paymentInfo = { ...paymentInfo };
			}
		} else {
			showAlert('Plan change cancelled.', 'info');
		}
	}

	function showAlert(message, type = 'info', duration = 5000) {
		alert = {
			show: true,
			message,
			type
		};

		setTimeout(() => {
			alert.show = false;
		}, duration);
	}

	async function navigateTo(view) {
		currentView = view;
		
		// Immediately clear local arrays to prevent showing stale cached data
		customers = [];
		products = [];
		pointsHistory = [];
		employees = [];
		
		// Fetch completely fresh data from the server
		try {
			await refreshData(true);
		} catch (error) {
			console.error("Error refreshing on navigation:", error);
		}
	}

	function setActiveLoginTab(tab) {
		activeLoginTab = tab;
	}

	async function loadCustomers() {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				customers = [];
				return;
			}

			if (isAdmin) {
				customers = [
					{ id: '1', name: 'Joel Jenkinson', email: 'joelj@gmail.com', cell: '0827999390', points: 7, has_reward: false, preferred_contact: JSON.stringify({ email: true, cell: false }) },
					{ id: '2', name: 'Jane Smith', email: 'jane@example.com', cell: '0987654321', points: 2, has_reward: false, preferred_contact: JSON.stringify({ email: true, cell: true }) },
					{ id: '3', name: 'Bob Johnson', email: 'bob@example.com', cell: '5554443333', points: 3, has_reward: false, preferred_contact: JSON.stringify({ email: false, cell: true }) }
				];
				return;
			}

			const response = await fetch(`${API_URL}/customers/index.php?_t=${Date.now()}`, {
				method: 'GET',
				headers: { 
					'Authorization': `Bearer ${token}`,
					'Cache-Control': 'no-cache'
				}
			});

			const data = await response.json();

			if (data.success) {
				customers = data.data.map(customer => {
					return {
						...customer,
						points: Number(customer.points) || 0,
						lifetime_points: customer.lifetime_points !== undefined && customer.lifetime_points !== null ? Number(customer.lifetime_points) : (Number(customer.points) || 0),
						lifetime_redemptions: customer.lifetime_redemptions !== undefined && customer.lifetime_redemptions !== null ? Number(customer.lifetime_redemptions) : 0,
						preferred_contact: customer.preferred_contact || JSON.stringify({ email: true, cell: false })
					};
				});
			} else {
				console.error('Error loading customers:', data.error);
				customers = [];
			}
		} catch (error) {
			console.error('Error loading customers:', error);
			showAlert('Failed to load customers', 'danger');
			customers = [];
		}
	}

	async function loadProducts() {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				products = [];
				return;
			}

			if (isAdmin) {
				products = [
					{ id: '1', name: 'Cup of Coffee', points_needed: 10 },
					{ id: '2', name: 'Muffin', points_needed: 7 },
					{ id: '3', name: 'Free Cake', points_needed: 20 }
				];
				return;
			}

			const response = await fetch(`${API_URL}/products/index.php`, {
				method: 'GET',
				headers: { 'Authorization': `Bearer ${token}` }
			});

			const data = await response.json();

			if (data.success) {
				products = data.data;
			} else {
				console.error('Error loading products:', data.error);
				products = [];
			}
		} catch (error) {
			console.error('Error loading products:', error);
			showAlert('Failed to load products', 'danger');
			products = [];
		}
	}

	async function loadPointsHistory() {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				pointsHistory = [];
				return;
			}

			if (isAdmin) {
				const now = new Date();
				const yesterday = new Date(now);
				yesterday.setDate(yesterday.getDate() - 1);
				pointsHistory = [
					{ id: '1', customer_name: 'Joel Jenkinson', customer_id: '1', action: 'Point Added', point_balance: 7, lifetime_points: 15, created_at: now.toISOString() },
					{ id: '2', customer_name: 'Jane Smith', customer_id: '2', action: 'Point Added', point_balance: 2, lifetime_points: 12, created_at: now.toISOString() },
					{ id: '3', customer_name: 'Bob Johnson', customer_id: '3', action: 'Point Added', point_balance: 3, lifetime_points: 8, created_at: yesterday.toISOString() }
				];
				return;
			}

			const timestamp = new Date().getTime();
			const response = await fetch(`${API_URL}/history/index.php?_t=${timestamp}`, {
				method: 'GET',
				headers: { 
					'Authorization': `Bearer ${token}`,
					'Cache-Control': 'no-cache'
				}
			});

			const data = await response.json();

			if (data.success) {
				pointsHistory = data.data;
			} else {
				pointsHistory = [];
			}
		} catch (error) {
			pointsHistory = [];
		}
	}

	async function loadMessageTemplates() {
		try {
			const token = localStorage.getItem('token');
			if (!token || isAdmin) return;

			const response = await fetch(`${API_URL}/settings/get_templates.php`, {
				method: 'GET',
				headers: { 'Authorization': `Bearer ${token}` }
			});

			const data = await response.json();

			if (data.success && data.templates) {
				messageTemplate = {
					pointEarned: data.templates.point_earned_template || messageTemplate.pointEarned,
					rewardAvailable: data.templates.reward_available_template || messageTemplate.rewardAvailable,
					pointAwardedAfterRedemption: data.templates.after_redemption_template || messageTemplate.pointAwardedAfterRedemption
				};
			} else {
				if (!data.success) console.error('Error loading message templates:', data.error);
			}
		} catch (error) {
			console.error('Error loading message templates:', error);
		}
	}

	// Employee Management Functions
	async function loadEmployees() {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				employees = [];
				return;
			}

			if (isAdmin) {
				employees = [
					{ id: '1', name: 'John Doe', email: 'john@example.com', permissions: { view_customers: true, add_customers: true, delete_customers: false, points_history: true, marketing_messages: false, full_access: false } },
					{ id: '2', name: 'Jane Smith', email: 'jane@example.com', permissions: { view_customers: true, add_customers: false, delete_customers: false, points_history: false, marketing_messages: false, full_access: false } }
				];
				return;
			}

			const response = await fetch(`${API_URL}/employees/index.php`, {
				method: 'GET',
				headers: { 'Authorization': `Bearer ${token}` }
			});

			const data = await response.json();

			if (data.success) {
				employees = data.data.map(employee => ({
					...employee,
					permissions: typeof employee.permissions === 'string' ? JSON.parse(employee.permissions) : employee.permissions
				}));
			} else {
				console.error('Error loading employees:', data.error);
				employees = [];
			}
		} catch (error) {
			console.error('Error loading employees:', error);
			showAlert('Failed to load employees', 'danger');
			employees = [];
		}
	}

	function openAddEmployeeModal() {
		employeeModalMode = 'add';
		newEmployee = {
			name: '',
			email: '',
			password: '',
			permissions: {
				delete_customers: false,
				edit_products: false,
				points_history: false,
				marketing_messages: false,
				full_access: false
			}
		};
		showEmployeeModal = true;
		document.body.style.overflow = 'hidden';
	}

	function openEditEmployeeModal(employee) {
		employeeModalMode = 'edit';
		selectedEmployee = employee;
		newEmployee = {
			name: employee.name,
			email: employee.email,
			password: '', // Don't pre-fill password for security
			permissions: { ...employee.permissions }
		};
		showEmployeeModal = true;
		document.body.style.overflow = 'hidden';
	}

	function closeEmployeeModal() {
		showEmployeeModal = false;
		selectedEmployee = null;
		newEmployee = {
			name: '',
			email: '',
			password: '',
			permissions: {
				delete_customers: false,
				edit_products: false,
				points_history: false,
				marketing_messages: false,
				full_access: false
			}
		};
		document.body.style.overflow = '';
	}

	async function saveEmployee() {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				showAlert('You must be logged in.', 'danger');
				return;
			}

			if (isAdmin) {
				// Admin demo logic
				if (employeeModalMode === 'add') {
					const newEmp = {
						id: Math.random().toString(36).substring(2, 9),
						...newEmployee
					};
					employees = [...employees, newEmp];
					showAlert(`Employee ${newEmployee.name} added successfully`, 'success');
				} else if (employeeModalMode === 'edit') {
					employees = employees.map(e => (e.id === selectedEmployee.id ? { ...selectedEmployee, ...newEmployee } : e));
					// If editing self, update currentUser permissions immediately
					if (selectedEmployee.id === currentUser?.id) {
						currentUser = { ...currentUser, permissions: newEmployee.permissions };
					}
					showAlert(`Employee ${newEmployee.name} updated successfully`, 'success');
				}
				closeEmployeeModal();
				return;
			}

			let response;
			if (employeeModalMode === 'add') {
				response = await fetch(`${API_URL}/employees/index.php`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`
					},
					body: JSON.stringify(newEmployee)
				});
			} else if (employeeModalMode === 'edit') {
				response = await fetch(`${API_URL}/employees/index.php`, {
					method: 'PUT',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`
					},
					body: JSON.stringify({ ...newEmployee, id: selectedEmployee.id })
				});
			}

			const data = await response.json();
			if (data.success) {
				if (employeeModalMode === 'add') {
					employees = [...employees, data.data];
				} else if (employeeModalMode === 'edit') {
					employees = employees.map(e => (e.id === selectedEmployee.id ? data.data : e));
					// If editing self, update currentUser permissions immediately
					if (selectedEmployee.id === currentUser?.id) {
						currentUser = { ...currentUser, permissions: data.data.permissions };
					}
				}
				showAlert(`Employee ${newEmployee.name} ${employeeModalMode === 'add' ? 'added' : 'updated'} successfully`, 'success');
				closeEmployeeModal();
			} else {
				showAlert(data.error || `Failed to ${employeeModalMode} employee`, 'danger');
			}
		} catch (error) {
			showAlert(`Error saving employee: ${error.message}`, 'danger');
		}
	}

	async function deleteEmployee(id) {
		if (!confirm('Are you sure you want to delete this employee? This action cannot be undone.')) return;

		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				showAlert('You must be logged in.', 'danger');
				return;
			}

			if (isAdmin) {
				// Admin demo logic
				employees = employees.filter(e => e.id !== id);
				showAlert('Employee deleted successfully', 'success');
				return;
			}

			const response = await fetch(`${API_URL}/employees/index.php?id=${id}`, {
				method: 'DELETE',
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});

			const data = await response.json();
			if (data.success) {
				employees = employees.filter(e => e.id !== id);
				showAlert('Employee deleted successfully', 'success');
			} else {
				showAlert(data.error || 'Failed to delete employee', 'danger');
			}
		} catch (error) {
			showAlert(`Error deleting employee: ${error.message}`, 'danger');
		}
	}

	// Store Branding Functions (Premium Feature)
	async function loadStoreBranding() {
		try {
			const token = localStorage.getItem('token');
			if (!token || isAdmin) return;

			const response = await fetch(`${API_URL}/settings/get_branding.php`, {
				method: 'GET',
				headers: { 'Authorization': `Bearer ${token}` }
			});

			const data = await response.json();
			if (data.success && data.branding) {
				storeBranding = {
					companyName: data.branding.companyName || '',
					logoUrl: data.branding.logoUrl || '',
					primaryColor: data.branding.primaryColor || '#30d5c8',
					secondaryColor: data.branding.secondaryColor || '#fc9403',
					useCustomBranding: data.branding.useCustomBranding || false
				};
				// Only apply custom colours if the toggle is ON
				if (storeBranding.useCustomBranding) {
					applyCustomColors(storeBranding.primaryColor, storeBranding.secondaryColor);
				} else {
					resetToDefaultColors();
				}
			}
		} catch (error) {
			console.error('Error loading store branding:', error);
		}
	}

	async function saveStoreBranding() {
		try {
			loading = true;
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				showAlert('You must be logged in.', 'danger');
				return;
			}

			if (paymentInfo.selectedTier !== 'premium') {
				showAlert('Store Branding is only available for Premium subscribers', 'warning');
				return;
			}

			if (isAdmin) {
				showAlert('Store branding settings saved (demo mode)', 'success');
				return;
			}

			const response = await fetch(`${API_URL}/settings/update_branding.php`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify(storeBranding)
			});

			const data = await response.json();
			if (data.success) {
				showAlert(
					storeBranding.useCustomBranding
						? 'Store branding activated and saved'
						: 'Store branding deactivated — Loyalty Pro defaults restored',
					'success'
				);
				// Apply or reset colours depending on toggle
				if (storeBranding.useCustomBranding) {
					applyCustomColors(storeBranding.primaryColor, storeBranding.secondaryColor);
				} else {
					resetToDefaultColors();
				}
			} else {
				showAlert(data.error || 'Failed to save branding settings', 'danger');
			}
		} catch (error) {
			showAlert(`Error saving branding settings: ${error.message}`, 'danger');
		} finally {
			loading = false;
		}
	}

	async function uploadLogo() {
		if (!logoFile) {
			showAlert('Please select a logo file first', 'warning');
			return;
		}

		try {
			isUploadingLogo = true;
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) {
				showAlert('You must be logged in.', 'danger');
				return;
			}

			if (paymentInfo.selectedTier !== 'premium') {
				showAlert('Logo upload is only available for Premium subscribers', 'warning');
				return;
			}

			if (isAdmin) {
				// Demo mode - create a fake URL
				storeBranding.logoUrl = 'https://via.placeholder.com/200x100/30d5c8/ffffff?text=Demo+Logo';
				storeBranding = { ...storeBranding };
				showAlert('Logo uploaded successfully (demo mode)', 'success');
				logoFile = null;
				return;
			}

			const formData = new FormData();
			formData.append('logo', logoFile);

			const response = await fetch(`${API_URL}/settings/upload_logo.php`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`
				},
				body: formData
			});

			const data = await response.json();
			if (data.success) {
				storeBranding.logoUrl = data.logoUrl;
				storeBranding = { ...storeBranding };
				showAlert('Logo uploaded successfully', 'success');
				logoFile = null;
			} else {
				showAlert(data.error || 'Failed to upload logo', 'danger');
			}
		} catch (error) {
			showAlert(`Error uploading logo: ${error.message}`, 'danger');
		} finally {
			isUploadingLogo = false;
		}
	}

	function handleLogoSelect(event) {
		const file = event.target.files[0];
		if (file) {
			// Validate file size (2MB max)
			if (file.size > 2 * 1024 * 1024) {
				showAlert('Logo file size must be less than 2MB', 'warning');
				return;
			}
			// Validate file type
			const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
			if (!allowedTypes.includes(file.type)) {
				showAlert('Invalid file type. Allowed types: JPEG, PNG, GIF, WebP, SVG', 'warning');
				return;
			}
			logoFile = file;
		}
	}

	function removeLogo() {
		storeBranding.logoUrl = '';
		storeBranding = { ...storeBranding };
		logoFile = null;
		showAlert('Logo removed. Remember to save your changes.', 'info');
	}

	function applyCustomColors(primary, secondary) {
		// Apply custom colors as CSS variables
		document.documentElement.style.setProperty('--primary', primary);
		document.documentElement.style.setProperty('--secondary', secondary);
	}

	function resetToDefaultColors() {
		// Revert to Loyalty Pro default colours
		document.documentElement.style.setProperty('--primary', '#30d5c8');
		document.documentElement.style.setProperty('--secondary', '#FF9F1C');
	}

	function openColorPicker(target) {
		colorPickerTarget = target;
		showColorPickerModal = true;
	}

	function closeColorPicker() {
		showColorPickerModal = false;
	}

	function selectColor(color) {
		if (colorPickerTarget === 'primary') {
			storeBranding.primaryColor = color;
		} else {
			storeBranding.secondaryColor = color;
		}
		storeBranding = { ...storeBranding };
		applyCustomColors(storeBranding.primaryColor, storeBranding.secondaryColor);
	}

	function applyTheme(mode) {
		// Legacy function - no longer used but kept for compatibility
		// Custom colors are now applied via applyCustomColors
	}

	async function saveMessageTemplates() {
		try {
			loading = true;
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }
			if (isAdmin) { showAlert('Message templates saved (demo).', 'success'); return; }
			const response = await fetch(`${API_URL}/settings/save_templates.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify(messageTemplate)
			});
			const data = await response.json();
			if (data.success) {
				showAlert('Message templates saved successfully', 'success');
			} else { showAlert(data.error || 'Failed to save templates', 'danger'); }
		} catch (error) { showAlert(`Error saving templates: ${error.message}`, 'danger'); }
		finally { loading = false; }
	}

	function openAddCustomerModal() {
		showCustomerModal = true;
		customerModalMode = 'add';
		newCustomer = {
			name: '', email: '', cell: '', points: 0, has_reward: false,
			preferred_contact: JSON.stringify({ email: true, cell: false })
		};
		preferredContactOptions = { email: true, cell: false };
	}

	function openEditCustomerModal(customer) {
		showCustomerModal = true;
		customerModalMode = 'edit';
		selectedCustomer = customer;
		newCustomer = {
			...customer,
			preferred_contact: customer.preferred_contact || JSON.stringify({ email: true, cell: false })
		};
		preferredContactOptions = parsePreferredContact(newCustomer);
	}

	function openViewCustomerModal(customer) {
		showCustomerModal = true;
		customerModalMode = 'view';
		selectedCustomer = customer;
	}

	function closeCustomerModal() {
		showCustomerModal = false;
		newCustomer = { name: '', email: '', cell: '', points: 0, preferred_contact: JSON.stringify({ email: true, cell: false }) };
		preferredContactOptions = { email: true, cell: false };
	}

	async function saveCustomer() {
		if (!newCustomer.name || (!newCustomer.email && !newCustomer.cell)) {
			showAlert('Name and either email or phone number are required', 'danger');
			return;
		}
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }

			newCustomer.preferred_contact = stringifyPreferredContact(preferredContactOptions);

			if (isAdmin) { /* ... admin demo logic ... */ closeCustomerModal(); return; }

			let response;
			if (customerModalMode === 'add') {
				response = await fetch(`${API_URL}/customers/index.php`, {
					method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
					body: JSON.stringify(newCustomer)
				});
			} else if (customerModalMode === 'edit') {
				response = await fetch(`${API_URL}/customers/customer.php?id=${selectedCustomer.id}`, {
					method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
					body: JSON.stringify({ ...newCustomer, min_points_needed: getMinPointsForFreebie() })
				});
			}
			const data = await response.json();
			if (data.success) {
				if (customerModalMode === 'add') {
					customers = [...customers, data.data];
				} else if (customerModalMode === 'edit') {
					customers = customers.map(c => (c.id === selectedCustomer.id ? data.data : c));
					await loadPointsHistory();
				}
				showAlert(`Customer ${newCustomer.name} ${customerModalMode === 'add' ? 'added' : 'updated'} successfully`, 'success');
				closeCustomerModal();
			} else {
				showAlert(data.error || `Failed to ${customerModalMode} customer`, 'danger');
			}
		} catch (error) { showAlert(`Error saving customer: ${error.message}`, 'danger'); }
	}

	async function deleteCustomer(id) {
		if (!confirm('Are you sure you want to delete this customer? This action cannot be undone.')) return;

		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }
			if (isAdmin) { 
				// Admin demo logic for deleting customer
				customers = customers.filter(c => c.id !== id);
				// Remove from points history
				pointsHistory = pointsHistory.filter(h => h.customer_id !== id);
				showAlert('Customer deleted successfully', 'success');
				return; 
			}

			const response = await fetch(`${API_URL}/customers/customer.php?id=${id}&_t=${Date.now()}`, {
				method: 'DELETE', 
				headers: { 
					'Authorization': `Bearer ${token}`,
					'Cache-Control': 'no-cache'
				}
			});
			const data = await response.json();
			if (data.success) {
				customers = customers.filter(c => c.id !== id);
				showAlert('Customer deleted successfully', 'success');
				await loadPointsHistory();
			} else {
				showAlert(data.error || 'Failed to delete customer', 'danger');
			}
		} catch (error) { showAlert(`Error deleting customer: ${error.message}`, 'danger'); }
	}

	async function addPoint(customer) {
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }
			
			if (isAdmin) { 
				// Admin demo logic
				const updatedCustomer = customers.find(c => c.id === customer.id);
				if (updatedCustomer) {
					updatedCustomer.points += 1;
					updatedCustomer.lifetime_points = (updatedCustomer.lifetime_points || 0) + 1;
					updatedCustomer.has_reward = updatedCustomer.points >= getMinPointsForFreebie();
					customers = [...customers]; // Trigger reactivity
					
					// Add to points history
					const now = new Date();
					pointsHistory = [
						{
							id: Math.random().toString(36).substring(2, 9),
							customer_id: customer.id,
							customer_name: customer.name,
							action: 'Point Added',
							point_balance: updatedCustomer.points,
							lifetime_points: updatedCustomer.lifetime_points,
							created_at: now.toISOString()
						},
						...pointsHistory
					];
					showAlert(`Point added for ${customer.name}`, 'success');
				}
				return; 
			}

			// OPTIMISTIC UPDATE: Update UI immediately before API call
			const previousPoints = customer.points;
			const previousLifetimePoints = customer.lifetime_points || 0;
			const newPoints = previousPoints + 1;
			const newLifetimePoints = previousLifetimePoints + 1;
			const hasRewardNow = newPoints >= getMinPointsForFreebie();

			// Update customer locally for instant visual feedback
			customers = customers.map(c =>
				(c.id === customer.id ? { 
					...c, 
					points: newPoints, 
					lifetime_points: newLifetimePoints, 
					has_reward: hasRewardNow 
				} : c)
			);

			// Show success toast immediately
			showAlert(`Point added for ${customer.name}`, 'success');

			// Add to local points history optimistically
			const now = new Date();
			const optimisticHistoryEntry = {
				id: 'temp-' + Math.random().toString(36).substring(2, 9),
				customer_id: customer.id,
				customer_name: customer.name,
				action: 'Point Added',
				point_balance: newPoints,
				lifetime_points: newLifetimePoints,
				created_at: now.toISOString()
			};
			pointsHistory = [optimisticHistoryEntry, ...pointsHistory];

			// API call happens in background
			const response = await fetch(`${API_URL}/customers/add_point.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ customer_id: customer.id })
			});
			const data = await response.json();
			
			if (data.success && data.data) {
				// Update with server-confirmed values (in case of any discrepancy)
				customers = customers.map(c =>
					(c.id === customer.id ? { 
						...c, 
						points: data.data.points, 
						lifetime_points: data.data.lifetime_points, 
						has_reward: data.data.has_reward 
					} : c)
				);

				// If email was sent, show second toast
				if (data.data.email_sent) {
					showAlert(`Email sent to ${customer.name}`, 'info', 3000);
				}
				
				// Refresh history in background (don't await to keep UI responsive)
				loadPointsHistory().catch(() => {});
			} else {
				// Rollback on error
				customers = customers.map(c =>
					(c.id === customer.id ? { 
						...c, 
						points: previousPoints, 
						lifetime_points: previousLifetimePoints, 
						has_reward: previousPoints >= getMinPointsForFreebie() 
					} : c)
				);
				// Remove optimistic history entry
				pointsHistory = pointsHistory.filter(h => h.id !== optimisticHistoryEntry.id);
				showAlert(data.error || 'Failed to add point', 'danger');
			}
		} catch (error) { 
			// On error, rollback would be handled by the caller if needed
			showAlert(`Error adding point: ${error.message}`, 'danger'); 
		}
	}

	async function addProduct() {
		if (paymentInfo.selectedTier === 'basic' && products.length >= 2) {
			showAlert('Basic plan allows a maximum of 2 products. Please upgrade to Premium to add more.', 'warning');
			navigateToPayment();
			return;
		}
		if (!newProduct.name || !newProduct.points_needed) {
			showAlert('Product name and points are required', 'danger'); return;
		}
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }
			if (isAdmin) { 
				// Admin demo logic for redeeming points
				const updatedCustomer = customers.find(c => c.id === selectedCustomer.id);
				if (updatedCustomer && selectedService) {
					// Deduct points for the service
					updatedCustomer.points -= selectedService.points_needed;
					updatedCustomer.has_reward = updatedCustomer.points >= getMinPointsForFreebie();
					customers = [...customers]; // Trigger reactivity
					
					// Add to points history
					const now = new Date();
					pointsHistory = [
						{
							id: Math.random().toString(36).substring(2, 9),
							customer_id: selectedCustomer.id,
							customer_name: selectedCustomer.name,
							action: `Redeemed ${selectedService.name}`,
							point_balance: updatedCustomer.points,
							lifetime_points: updatedCustomer.lifetime_points,
							created_at: now.toISOString()
						},
						...pointsHistory
					];
					showAlert(`Freebie redeemed for ${selectedCustomer.name}`, 'success');
					closeRedeemModal();
				}
				return; 
			}
			const response = await fetch(`${API_URL}/products/index.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify(newProduct)
			});
			const data = await response.json();
			if (data.success) {
				products = [...products, data.data];
				showAlert(`${newProduct.name} added successfully`, 'success');
				newProduct = { name: '', points_needed: 10 };
			} else { showAlert(data.error || 'Failed to add product', 'danger'); }
		} catch (error) { showAlert(`Error adding product: ${error.message}`, 'danger'); }
	}

	async function deleteProduct(id) {
		if (!confirm('Are you sure you want to delete this product? This action cannot be undone.')) return;
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }
			if (isAdmin) { /* ... admin demo ... */ return; }
			const response = await fetch(`${API_URL}/products/product.php?id=${id}`, {
				method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
			});
			const data = await response.json();
			if (data.success) {
				products = products.filter(p => p.id !== id);
				showAlert('Product deleted successfully', 'success');
			} else { showAlert(data.error || 'Failed to delete product', 'danger'); }
		} catch (error) { showAlert(`Error deleting product: ${error.message}`, 'danger'); }
	}

	function openRedeemModal(customer) {
		selectedCustomer = customer;
		showRedeemModal = true;
		selectedService = products.find(p => customer.points >= p.points_needed) || null;
	}

	function closeRedeemModal() {
		showRedeemModal = false;
		selectedCustomer = null;
		selectedService = null;
	}

	function selectService(product) {
		selectedService = product;
	}

	async function redeemFreebie() {
		if (!selectedCustomer || !selectedService) {
			showAlert('Please select a service to redeem', 'danger'); return;
		}
		if (selectedCustomer.points < selectedService.points_needed) {
			showAlert('Not enough points to redeem this service', 'danger'); return;
		}
		try {
			const token = localStorage.getItem('token');
			if (!token && !isAdmin) { showAlert('You must be logged in.', 'danger'); return; }
			if (isAdmin) { /* ... admin demo ... */ return; }

			// OPTIMISTIC UPDATE: Update UI immediately before API call
			const previousPoints = selectedCustomer.points;
			const previousLifetimeRedemptions = selectedCustomer.lifetime_redemptions || 0;
			const pointsNeeded = selectedService.points_needed;
			const newPoints = previousPoints - pointsNeeded;
			const newLifetimeRedemptions = previousLifetimeRedemptions + 1;
			const hasRewardNow = newPoints >= getMinPointsForFreebie();

			const customerId = selectedCustomer.id;
			const customerName = selectedCustomer.name;
			const productName = selectedService.name;
			const selectedServiceId = selectedService.id;
			const previousLifetimePoints = selectedCustomer.lifetime_points || 0;

			// Update customer locally for instant visual feedback
			customers = customers.map(c =>
				(c.id === customerId ? { 
					...c, 
					points: newPoints, 
					lifetime_redemptions: newLifetimeRedemptions,
					has_reward: hasRewardNow 
				} : c)
			);

			// Show success toast immediately and close modal
			showAlert(`Freebie redeemed for ${customerName}`, 'success');
			closeRedeemModal();

			// Add to local points history optimistically
			const now = new Date();
			const optimisticHistoryEntry = {
				id: 'temp-' + Math.random().toString(36).substring(2, 9),
				customer_id: customerId,
				customer_name: customerName,
				action: `Redeemed ${productName}`,
				redeemed_item: `${productName} (${pointsNeeded} Points)`,
				point_balance: newPoints,
				lifetime_points: previousLifetimePoints,
				lifetime_redemptions: newLifetimeRedemptions,
				created_at: now.toISOString()
			};
			pointsHistory = [optimisticHistoryEntry, ...pointsHistory];

			// API call happens in background
			const response = await fetch(`${API_URL}/customers/redeem.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ customer_id: customerId, product_id: selectedServiceId })
			});
			const data = await response.json();
			
			if (data.success && data.data) {
				// Update with server-confirmed values (in case of any discrepancy)
				customers = customers.map(c =>
					(c.id === customerId ? { 
						...c, 
						points: data.data.points, 
						lifetime_points: data.data.lifetime_points !== undefined ? data.data.lifetime_points : c.lifetime_points,
						lifetime_redemptions: data.data.lifetime_redemptions !== undefined ? data.data.lifetime_redemptions : c.lifetime_redemptions,
						has_reward: data.data.has_reward 
					} : c)
				);

				// If email was sent, show second toast
				if (data.data.email_sent) {
					showAlert(`Email sent to ${customerName}`, 'info', 3000);
				}
				
				// Refresh history in background (don't await to keep UI responsive)
				loadPointsHistory().catch(() => {});
			} else {
				// Rollback on error - restore previous state
				customers = customers.map(c =>
					(c.id === customerId ? { 
						...c, 
						points: previousPoints, 
						lifetime_redemptions: previousLifetimeRedemptions,
						has_reward: previousPoints >= getMinPointsForFreebie() 
					} : c)
				);
				// Remove optimistic history entry
				pointsHistory = pointsHistory.filter(h => h.id !== optimisticHistoryEntry.id);
				showAlert(data.error || 'Failed to redeem freebie', 'danger');
			}
		} catch (error) { 
			showAlert(`Error redeeming freebie: ${error.message}`, 'danger'); 
		}
	}

	function handlePayment() {
		showAlert('Payment feature will be available soon. Thank you for your patience.', 'info');
	}

	async function sendPointNotification(customer) {
		const token = localStorage.getItem('token');
		if (!token || isAdmin) return;

		try {
			const notificationType = calculateFreebies(customer.points) > 0
				? 'reward_available'
				: 'point_earned';

			const response = await fetch(`${API_URL}/notifications/send_email.php`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ customer_id: customer.id, notification_type: notificationType })
			});
			const data = await response.json();
			if (data.success) {
				console.log(`Attempted to send ${notificationType} email for customer ${customer.id}. Success: ${data.message}`);
			} else {
				console.error('Error triggering notification email:', data.error || data.message);
			}
		} catch (error) {
			console.error('Error triggering notification email:', error);
		}
	}

	async function sendRedemptionNotification(customer, service) {
		const token = localStorage.getItem('token');
		if (!token || isAdmin) return;

		try {
			const response = await fetch(`${API_URL}/notifications/send_email.php`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({
					customer_id: customer.id,
					notification_type: 'after_redemption',
					product_name: service.name
				})
			});
			const data = await response.json();
			if (data.success) {
				// console.log(`Attempted to send after_redemption email for customer ${customer.id}. Success: ${data.message}`);
			} else {
				console.error('Error triggering redemption notification email:', data.error || data.message);
			}
		} catch (error) {
			console.error('Error triggering redemption notification email:', error);
		}
	}

	onMount(async () => {
		const isAuthStored = localStorage.getItem('isAuthenticated') === 'true';
		const isAdminStored = localStorage.getItem('isAdmin') === 'true';
		const token = localStorage.getItem('token');
		const storedUserData = localStorage.getItem('userData');
		const storedBusinessCreatedAt = localStorage.getItem('businessCreatedAt');

		checkForPasswordResetToken();

		if (isAuthStored) {
			isAuthenticated = true;
			if (isAdminStored) {
				isAdmin = true;
				currentView = 'customers';
			} else if (token && storedUserData) {
				try {
					currentUser = JSON.parse(storedUserData);
					if (currentUser && !currentUser.business_created_at && storedBusinessCreatedAt) {
						currentUser.business_created_at = storedBusinessCreatedAt;
					}
					currentView = 'customers';
					if (currentUser && currentUser.package) {
						paymentInfo.selectedTier = currentUser.package.toLowerCase();
						localStorage.setItem('subscriptionTier', paymentInfo.selectedTier);
					} else {
						paymentInfo.selectedTier = localStorage.getItem('subscriptionTier') || 'basic';
					}
					// Load data with error handling to prevent breaking the app
					try { await loadCustomers(); } catch (e) { console.error('Failed to load customers:', e); customers = []; }
					try { await loadProducts(); } catch (e) { console.error('Failed to load products:', e); products = []; }
					try { await loadPointsHistory(); } catch (e) { console.error('Failed to load history:', e); pointsHistory = []; }
					try { await loadMessageTemplates(); } catch (e) { console.error('Failed to load templates:', e); messageTemplates = {}; }
					try { await loadStoreBranding(); } catch (e) { console.error('Failed to load store branding:', e); }
				} catch (e) {
					console.error('Error parsing user data onMount:', e);
					logout();
				}
			}
		} else {
			currentView = 'login';
		}

		if (isAuthenticated || isAdmin) {
			paymentInfo.businessCreatedAt = currentUser?.business_created_at || storedBusinessCreatedAt;
			paymentInfo.lastPaymentDate = localStorage.getItem('lastPaymentDate');
			checkPaymentStatus();
			paymentInfo = { ...paymentInfo };
		}

		if (isAdmin && (!customers || !customers.length)) {
			customers = [
				{ id: '1', name: 'Joel Jenkinson', email: 'joelj@gmail.com', cell: '0827999390', points: 7, has_reward: false, preferred_contact: JSON.stringify({ email: true, cell: false }) },
				{ id: '2', name: 'Jane Smith', email: 'jane@example.com', cell: '0987654321', points: 2, has_reward: false, preferred_contact: JSON.stringify({ email: true, cell: true }) },
				{ id: '3', name: 'Bob Johnson', email: 'bob@example.com', cell: '5554443333', points: 3, has_reward: false, preferred_contact: JSON.stringify({ email: false, cell: true }) }
			];
		}
		setupPullToRefresh();
	});

	function checkForPasswordResetToken() {
		const urlParams = new URLSearchParams(window.location.search);
		const token = urlParams.get('token');
		if (token) {
			resetPasswordData.token = token;
			currentView = 'reset-password';
			verifyResetToken(token);
		}
	}

	async function verifyResetToken(token) {
		try {
			resetPasswordState = 'loading';
			const response = await fetch(`${API_URL}/auth/verify-reset-token.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token })
			});
			const data = await response.json();
			if (data.success) {
				try {
					const completeResponse = await fetch(`${API_URL}/auth/complete-reset.php`, {
						method: 'POST', headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ token })
					});
					const completeData = await completeResponse.json();
					if (completeData.success) {
						resetPasswordState = 'success';
						showAlert(completeData.message || 'Password has been reset successfully.', 'success');
					} else {
						resetPasswordState = 'invalid';
						showAlert(completeData.error || 'Failed to finalize password reset.', 'danger');
					}
				} catch (completeError) {
					console.error('Error finalizing reset:', completeError);
					resetPasswordState = 'invalid';
					showAlert('Error finalizing password reset.', 'danger');
				}
			} else {
				resetPasswordState = 'invalid';
				showAlert(data.error || 'Invalid or expired reset token.', 'danger');
			}
		} catch (error) {
			console.error('Error verifying token:', error);
			resetPasswordState = 'invalid';
			showAlert('Error processing reset request.', 'danger');
		}
	}

	async function completePasswordReset() {
		// This function is called by verifyResetToken in the compiled flow.
		// The password fields in the reset-password view are not used for this flow in the compiled code.
		// The compiled code's complete-reset.php endpoint handles setting the password using the token alone.
		try {
			loading = true;
			const response = await fetch(`${API_URL}/auth/complete-reset.php`, {
				method: 'POST', headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token: resetPasswordData.token }) // Only token is sent
			});
			const data = await response.json();
			if (data.success) {
				resetPasswordState = 'success';
				showAlert('Password has been reset successfully.', 'success');
			} else { showAlert(data.error || 'Failed to reset password.', 'danger'); }
		} catch (error) {
			showAlert(`Error resetting password: ${error.message}`, 'danger');
		} finally { loading = false; }
	}

	// A11y helper for keyboard navigation
	function handleKeydownAsClick(event, action) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			action();
		}
	}
	function handleKeydownAsClickOrEscape(event, clickAction, escapeAction) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			clickAction();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			escapeAction();
		}
	}

	// Extract first name from a full name string
	function getFirstName(fullName) {
		if (!fullName) return null;
		return fullName.trim().split(/\s+/)[0];
	}
</script>

<div class="container">
	<!-- Desktop Navigation (Authenticated) -->
	<div class="nav desktop-nav {storeBranding.logoUrl && storeBranding.useCustomBranding && paymentInfo.selectedTier === 'premium' ? 'has-premium-logo' : ''}" style="display: {isAuthenticated ? 'flex' : 'none'}">
		<div class="nav-logo-container">
			{#if storeBranding.logoUrl && storeBranding.useCustomBranding && paymentInfo.selectedTier === 'premium'}
				<!-- Premium: Show their logo instead of Loyalty Pro text -->
				<div class="nav-logo-premium">
					<img src={storeBranding.logoUrl} alt="{storeBranding.companyName || 'Store'}" class="nav-logo-img" />
				</div>
			{:else}
				<!-- Basic: Show Loyalty Pro branding -->
				<div class="nav-logo">Loyalty <span class="text-white">Pro</span></div>
			{/if}
		</div>
		<div class="nav-links">
			{#if currentUser && getFirstName(currentUser.name)}
				<span class="nav-greeting">Hello {getFirstName(currentUser.name)}!</span>
			{/if}
			<span class="nav-link" role="button" tabindex="0" on:click={() => navigateTo('admin')} on:keydown={(e) => handleKeydownAsClick(e, () => navigateTo('admin'))}>Admin</span>
			<span class="nav-link" role="button" tabindex="0" on:click={() => navigateTo('customers')} on:keydown={(e) => handleKeydownAsClick(e, () => navigateTo('customers'))}>Customers</span>
			<span class="nav-link" role="button" tabindex="0" on:click={logout} on:keydown={(e) => handleKeydownAsClick(e, logout)}>Logout</span>
		</div>
	</div>

	<!-- Mobile Bottom Navigation (Authenticated) -->
	{#if isAuthenticated}
		<div class="mobile-nav">
			<div class="mobile-nav-item" class:active={currentView === 'customers'} role="button" tabindex="0" on:click={() => navigateTo('customers')} on:keydown={(e) => handleKeydownAsClick(e, () => navigateTo('customers'))}>
				<svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 20 20" fill="currentColor">
					<path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
				</svg>
				<span>Customers</span>
			</div>
			<div class="mobile-nav-item" class:active={currentView === 'admin'} role="button" tabindex="0" on:click={() => navigateTo('admin')} on:keydown={(e) => handleKeydownAsClick(e, () => navigateTo('admin'))}>
				<svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 20 20" fill="currentColor">
					<path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
				</svg>
				<span>Admin</span>
			</div>
			<div class="mobile-nav-item" role="button" tabindex="0" on:click={logout} on:keydown={(e) => handleKeydownAsClick(e, logout)}>
				<svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 20 20" fill="currentColor">
					<path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414l-5-5H3zm7 3a1 1 0 00-1 1v3a1 1 0 002 0V7a1 1 0 00-1-1z" clip-rule="evenodd" />
					<path d="M14 7v1h-4v4h4v1l2-3-2-3z" />
				</svg>
				<span>Logout</span>
			</div>
		</div>
	{/if}

	{#if isRefreshing}
		<div class="loading-indicator">
			<div class="spinner"></div>
			<p>Refreshing...</p>
		</div>
	{/if}

	<div class="alert-container">
		{#if alert.show}
			<div class="alert alert-{alert.type} {(isAuthenticated || (currentView === 'login' && !isAuthenticated)) ? 'has-mobile-nav' : ''}">
				{alert.message}
			</div>
		{/if}
	</div>

	{#if currentView === 'login'}
		<!-- Marketing Site Navigation (Unauthenticated) -->
		<div class="nav desktop-nav">
			<div class="nav-logo">Loyalty <span class="text-white">Pro</span></div>
			<div class="nav-links">
				<a href="{MARKETING_SITE_URL}/#features" class="nav-link">Features</a>
				<a href="{MARKETING_SITE_URL}/#pricing" class="nav-link">Pricing</a>
				<a href="{MARKETING_SITE_URL}/#why-us" class="nav-link">Why Us</a>
			</div>
		</div>
		<!-- Mobile Marketing Nav (Unauthenticated - Bottom Bar) -->
		{#if !isAuthenticated}
			<div class="mobile-nav">
				<a href="{MARKETING_SITE_URL}/#features" class="mobile-nav-item"><span>Features</span></a>
				<a href="{MARKETING_SITE_URL}/#pricing" class="mobile-nav-item"><span>Pricing</span></a>
				<a href="{MARKETING_SITE_URL}/#why-us" class="mobile-nav-item"><span>Why Us</span></a>
			</div>
		{/if}

		<div class="card dark-card max-w-md mx-auto">
			<div class="tabs">
				<div
					class="tab {activeLoginTab === 'login' ? 'active' : ''}"
					role="button" tabindex="0"
					on:click={() => setActiveLoginTab('login')}
					on:keydown={(e) => handleKeydownAsClick(e, () => setActiveLoginTab('login'))}
				>
					Login
				</div>
				<div
					class="tab {activeLoginTab === 'signup' ? 'active' : ''}"
					role="button" tabindex="0"
					on:click={() => setActiveLoginTab('signup')}
					on:keydown={(e) => handleKeydownAsClick(e, () => setActiveLoginTab('signup'))}
				>
					Sign Up
				</div>
			</div>

			{#if activeLoginTab === 'login'}
				<h2 class="text-center mb-4">Login</h2>
				<form on:submit|preventDefault={login}>
					<div class="form-group">
						<label class="form-label" for="userEmail">Email</label>
						<input
							id="userEmail"
							type="email"
							class="form-input dark-input"
							bind:value={user.email}
							required
						/>
					</div>

					<div class="form-group">
						<label class="form-label" for="userPassword">Password</label>
						<div class="password-input-container">
							<input
								id="userPassword"
								type="password"
								use:passwordVisibility={showPassword}
								class="form-input dark-input"
								bind:value={user.password}
								required
							/>
							<button
								type="button"
								class="password-toggle"
								on:click={togglePasswordVisibility}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
							>
								{#if showPassword}
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-2.891 0-5.729-1.295-7.841-3.821C2.947 13.635 2 11.473 2 10c0-1.473.947-3.635 2.159-5.179a10.099 10.099 0 013.455-2.849m10.88 2.849C19.753 6.365 20.7 8.527 20.7 10c0 1.473-.947 3.635-2.159 5.179C16.729 17.705 13.891 19 11 19c-.498 0-.991-.05-1.472-.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L16 7.5 14.5 6l-4.5 4.5a3 3 0 00-2.121 5.121L3 21l1.5 1.5 5.379-5.379z" />
									</svg>
								{:else}
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
									</svg>
								{/if}
							</button>
						</div>

						<small class="text-muted">For demo: use admin@loyaltypro.io / Password</small>
					</div>

					<button type="submit" class="btn btn-primary w-full mb-3" disabled={loading}>
						{loading ? 'Logging in...' : 'Login'}
					</button>
					<div class="text-center">
						<button type="button" class="btn-link" on:click={openForgotPasswordModal}>Forgot Password?</button>
					</div>
				</form>
			{/if}

			{#if activeLoginTab === 'signup'}
				<h2 class="text-center mb-4">Business Registration</h2>
				<form on:submit|preventDefault={registerBusiness}>
					<div class="form-group">
						<label class="form-label" for="ownerName">Owner's Name</label>
						<input
							id="ownerName"
							type="text"
							class="form-input dark-input"
							bind:value={businessRegistration.owner_name}
							required
						/>
					</div>

					<div class="form-group">
						<label class="form-label" for="businessName">Business Name</label>
						<input
							id="businessName"
							type="text"
							class="form-input dark-input"
							bind:value={businessRegistration.business_name}
							required
						/>
					</div>

					<div class="form-group">
						<label class="form-label" for="ownerEmail">Owner's Email</label>
						<input
							id="ownerEmail"
							type="email"
							class="form-input dark-input"
							bind:value={businessRegistration.owner_email}
							required
						/>
					</div>

					<div class="form-group">
						<label class="form-label" for="ownerCell">Owner's Cell</label>
						<input
							id="ownerCell"
							type="tel"
							class="form-input dark-input"
							bind:value={businessRegistration.owner_cell}
						/>
					</div>

					<div class="form-group">
						<label class="form-label" for="regPassword">Password</label>
						<div class="password-input-container">
							<input
								id="regPassword"
								type="password"
								use:passwordVisibility={showPassword}
								class="form-input dark-input"
								bind:value={businessRegistration.password}
								required
							/>
							<button
								type="button"
								class="password-toggle"
								on:click={togglePasswordVisibility}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
							>
								{#if showPassword}
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-2.891 0-5.729-1.295-7.841-3.821C2.947 13.635 2 11.473 2 10c0-1.473.947-3.635 2.159-5.179a10.099 10.099 0 013.455-2.849m10.88 2.849C19.753 6.365 20.7 8.527 20.7 10c0 1.473-.947 3.635-2.159 5.179C16.729 17.705 13.891 19 11 19c-.498 0-.991-.05-1.472-.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L16 7.5 14.5 6l-4.5 4.5a3 3 0 00-2.121 5.121L3 21l1.5 1.5 5.379-5.379z" />
									</svg>
								{:else}
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
									</svg>
								{/if}
							</button>
						</div>
					</div>

					<div class="form-group">
						<label class="form-label" for="confirmPassword">Repeat Password</label>
						<div class="password-input-container">
							<input
								id="confirmPassword"
								type="password"
								use:passwordVisibility={showConfirmPassword}
								class="form-input dark-input"
								bind:value={businessRegistration.confirmPassword}
								required
							/>
							<button
								type="button"
								class="password-toggle"
								on:click={toggleConfirmPasswordVisibility}
								aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
							>
								{#if showConfirmPassword}
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-2.891 0-5.729-1.295-7.841-3.821C2.947 13.635 2 11.473 2 10c0-1.473.947-3.635 2.159-5.179a10.099 10.099 0 013.455-2.849m10.88 2.849C19.753 6.365 20.7 8.527 20.7 10c0 1.473-.947 3.635-2.159 5.179C16.729 17.705 13.891 19 11 19c-.498 0-.991-.05-1.472-.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L16 7.5 14.5 6l-4.5 4.5a3 3 0 00-2.121 5.121L3 21l1.5 1.5 5.379-5.379z" />
									</svg>
								{:else}
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
									</svg>
								{/if}
							</button>
						</div>
					</div>

					<button type="submit" class="btn btn-primary w-full" disabled={loading}>
						{loading ? 'Creating Account...' : 'Sign Up'}
					</button>
				</form>
			{/if}
		</div>
	{/if}

	{#if currentView === 'admin'}
		<div class="card dark-card">
			<h2 class="mb-4">Admin Dashboard</h2>

			<div class="admin-section">
				<div class="admin-section-header"
					on:click={() => toggleSection('payment')}
					on:keydown={(e) => handleKeydownAsClick(e, () => toggleSection('payment'))}
					role="button" tabindex="0">
					<h3>
						<svg xmlns="http://www.w3.org/2000/svg" class="section-icon" viewBox="0 0 20 20" fill="currentColor">
							<path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
							<path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd" />
						</svg>
						Subscription & Payment
					</h3>
					<span class="toggle-icon">{expandedSection === 'payment' ? '−' : '+'}</span>
				</div>

				{#if expandedSection === 'payment'}
					<div class="admin-section-content">
						<div class="mb-4">
							<h4 class="mb-3">Choose Your Subscription Plan</h4>
							<p class="mb-3">Select the plan that best suits your business needs:</p>

							<div class="subscription-tiers">
								<div class="subscription-tier {paymentInfo.selectedTier === 'basic' ? 'selected' : ''}">
									<div class="tier-header">
										<h5>Basic</h5>
										<div class="tier-price">R49/month</div>
									</div>
									<div class="tier-features">
										<div class="tier-feature">✓ 2 Month Free Trial</div>
										<div class="tier-feature">✓ Customer Loyalty Tracking</div>
										<div class="tier-feature">✓ Points Management</div>
										<div class="tier-feature">✓ Freebie Redemption</div>
										<div class="tier-feature">✓ Customer Points History</div>
										<div class="tier-feature">✓ 2 Freebie Products</div>
									</div>
									<button class="btn btn-primary w-full mt-3" on:click={() => selectSubscriptionTier('basic')}>
										{paymentInfo.selectedTier === 'basic' ? 'Current Plan' : 'Select Basic'}
									</button>
								</div>

								<div class="subscription-tier {paymentInfo.selectedTier === 'premium' ? 'selected' : ''}">
									<div class="tier-badge">RECOMMENDED</div>
									<div class="tier-header">
										<h5>Premium</h5>
										<div class="tier-price">R159/month</div>
									</div>
									<div class="tier-features">
										<div class="tier-feature">✓ 2 Month Free Trial</div>
										<div class="tier-feature">✓ All Basic Features</div>
										<div class="tier-feature">✓ Email Notifications</div>
										<div class="tier-feature">✓ Marketing Blasts (Email)</div>
										<div class="tier-feature">✓ CSV Export for History</div>
										<div class="tier-feature">✓ Unlimited Products</div>
									</div>
									<button class="btn btn-secondary w-full mt-3" on:click={() => selectSubscriptionTier('premium')}>
										{paymentInfo.selectedTier === 'premium' ? 'Current Plan' : 'Select Premium'}
									</button>
								</div>
							</div>

							<div class="current-subscription mt-4">
								<h4 class="mb-2">Your Subscription</h4>
								<div class="payment-status-card payment-status-{paymentInfo.status}">
									{#if paymentInfo.status === 'free'}
										<div class="payment-status-icon">✓</div>
										<div class="payment-status-content">
											<h5>Free Trial Active</h5>
											<p>Your free trial for the <strong>{paymentInfo.selectedTier} plan</strong> is active until {formatDate(paymentInfo.trialEndDate)}.</p>
											<p>Your first bill of R{paymentInfo.amountDue} will be on {formatDate(paymentInfo.nextBillingDate)}.</p>
										</div>
									{:else if paymentInfo.status === 'paid'}
										<div class="payment-status-icon">✓</div>
										<div class="payment-status-content">
											<h5>Subscription Active</h5>
											<p>Your <strong>{paymentInfo.selectedTier} plan</strong> is active.</p>
											<p>Next payment: R{paymentInfo.amountDue} on {formatDate(paymentInfo.nextBillingDate)}.</p>
										</div>
									{:else if paymentInfo.status === 'due'}
										<div class="payment-status-icon">!</div>
										<div class="payment-status-content">
											<h5>Payment Due</h5>
											<p>Your payment for the <strong>{paymentInfo.selectedTier} plan</strong> is due.</p>
											<p>Amount due: R{paymentInfo.amountDue} (Due on {formatDate(paymentInfo.nextBillingDate)})</p>
											<button class="btn btn-primary mt-3 w-full" on:click={handlePayment}>Pay Now</button>
										</div>
									{:else if paymentInfo.status === 'unknown' || paymentInfo.status === 'error'}
										<div class="payment-status-icon">?</div>
										<div class="payment-status-content">
											<h5>Subscription Status</h5>
											<p>Could not determine your subscription status. Please ensure you are logged in or contact support if this persists.</p>
										</div>
									{/if}
								</div>
							</div>
						</div>
					</div>
				{/if}
			</div>

			<div class="admin-section">
				<div class="admin-section-header"
					on:click={() => toggleSection('history')}
					on:keydown={(e) => handleKeydownAsClick(e, () => toggleSection('history'))}
					role="button" tabindex="0">
					<h3>
						<svg xmlns="http://www.w3.org/2000/svg" class="section-icon" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
						</svg>
						Customer Points History
					</h3>
					<span class="toggle-icon">{expandedSection === 'history' ? '−' : '+'}</span>
				</div>

				{#if expandedSection === 'history'}
					<div class="admin-section-content">
						<div class="admin-card mb-4">
							<div class="flex justify-between items-center mb-3">
								<h4>Transaction History</h4>
								<div class="flex gap-2">
									{#if paymentInfo.selectedTier === 'premium'}
										<button class="btn btn-primary btn-sm" on:click={exportToCsv}>Download CSV</button>
										<button class="btn btn-secondary btn-sm" on:click={emailPointsSummary}>Email Points Summary</button>
									{:else}
										<button class="btn btn-primary btn-sm" disabled title="Upgrade to Premium to export CSV">Download CSV (Premium)</button>
									{/if}
								</div>
							</div>

							<div class="history-table-container">
								<table class="history-table">
								<thead>
									<tr>
										<th>Customer</th>
										<th>Action</th>
										<th>Redeemed</th>
										<th>Balance</th>
										<th>Lifetime Points</th>
										<th>Lifetime Freebies</th>
										<th>Date</th>
										<th>Time</th>
									</tr>
								</thead>
									<tbody>
										{#if pointsHistory.length === 0}
											<tr>
										<td colspan="8" class="text-center">No transaction history available.</td>
									</tr>
										{:else}
											{#each pointsHistory as entry (entry.id)}
												<tr>
										<td>{entry.customer_name}</td>
										<td>{entry.action}</td>
										<td>{entry.redeemed_item || ''}</td>
										<td>{entry.point_balance} Points</td>
										<td>{entry.lifetime_points || 0} Points</td>
										<td>{entry.lifetime_redemptions || 0}</td>
										<td>{formatDate(entry.created_at)}</td>
										<td>{formatTime(entry.created_at)}</td>
									</tr>
											{/each}
										{/if}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				{/if}
			</div>

			<div class="admin-section">
				<div class="admin-section-header"
					on:click={() => toggleSection('products')}
					on:keydown={(e) => handleKeydownAsClick(e, () => toggleSection('products'))}
					role="button" tabindex="0">
					<h3>
						<svg xmlns="http://www.w3.org/2000/svg" class="section-icon" viewBox="0 0 20 20" fill="currentColor">
							<path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
						</svg>
						Products & Services
						{#if paymentInfo.selectedTier === 'basic' && products.length >= 2}
							<span class="badge badge-secondary" style="margin-left: 10px; font-size: 0.7rem; background-color: var(--danger); color: white; padding: 0.2em 0.6em; border-radius: 10px;">Limit Reached</span>
						{/if}
					</h3>
					<span class="toggle-icon">{expandedSection === 'products' ? '−' : '+'}</span>
				</div>

				{#if expandedSection === 'products'}
					<div class="admin-section-content">
						<p class="mb-3">Configure the products or services that customers can earn freebies for.</p>

						<div class="admin-card mb-4">
							<h4 class="mb-3">Add a Product or Service</h4>
							{#if paymentInfo.selectedTier === 'premium' || (paymentInfo.selectedTier === 'basic' && products.length < 2)}
								<form on:submit|preventDefault={addProduct}>
									<div class="form-group">
										<label class="form-label" for="productName">Product/Service Name</label>
										<input
											id="productName"
											type="text"
											class="form-input dark-input"
											bind:value={newProduct.name}
											placeholder="e.g. Cup of Coffee"
											required
										/>
									</div>
									<div class="form-group">
										<label class="form-label" for="pointsNeeded">Points for a Freebie</label>
										<input
											id="pointsNeeded"
											type="number"
											class="form-input dark-input"
											bind:value={newProduct.points_needed}
											min="1"
											required
										/>
									</div>
									<button type="submit" class="btn btn-primary w-full" disabled={loading}>Add Product</button>
								</form>
							{:else}
								<div class="feature-locked-message">
									<p>🛍️ Want to add more than 2 products?</p>
									<p>Our <strong>Basic plan</strong> allows up to 2 products. To add more, please upgrade to Premium.</p>
									<button class="btn btn-secondary mt-3" on:click={navigateToPayment}>Upgrade to Premium</button>
								</div>
							{/if}
						</div>

						<div class="admin-card">
							<h4 class="mb-3">Your Products & Services</h4>
							{#if products.length === 0}
								<p>No products configured yet. Add your first product above.</p>
							{:else}
								<div class="product-list">
									{#each products as product (product.id)}
										<div class="product-item">
											<div class="product-details">
												<strong>{product.name}</strong>
												<div>{product.points_needed} points required</div>
											</div>
											<button class="btn btn-danger btn-sm" on:click={() => deleteProduct(product.id)} disabled={loading}>Delete</button>
										</div>
									{/each}
								</div>
							{/if}
						</div>
					</div>
				{/if}
			</div>

			<div class="admin-section">
				<div class="admin-section-header"
					on:click={() => toggleSection('marketing')}
					on:keydown={(e) => handleKeydownAsClick(e, () => toggleSection('marketing'))}
					role="button" tabindex="0">
					<h3>
						<svg xmlns="http://www.w3.org/2000/svg" class="section-icon" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clip-rule="evenodd" />
						</svg>
						Marketing Messages
						{#if paymentInfo.selectedTier === 'basic'}
							<span class="badge badge-secondary" style="margin-left: 10px; font-size: 0.7rem; background-color: var(--secondary); color: white; padding: 0.2em 0.6em; border-radius: 10px;">Premium Feature</span>
						{/if}
					</h3>
					<span class="toggle-icon">{expandedSection === 'marketing' ? '−' : '+'}</span>
				</div>

				{#if expandedSection === 'marketing'}
					<div class="admin-section-content">
						{#if paymentInfo.selectedTier === 'premium'}
							<p class="mb-4">Send promotional emails to all of your customers with valid email addresses who have opted to receive emails.</p>
							<div class="form-group">
								<label class="form-label" for="blastMessage">Message to all customers</label>
								<textarea
									id="blastMessage"
									class="form-input dark-input"
									bind:value={blastMessage}
									rows="3"
									placeholder="Enter your promotional message here..."
								></textarea>
								<small>Emails will be sent only to customers who have chosen to receive emails.</small>
							</div>
							<button
								class="btn btn-primary w-full"
								on:click={openEmailConfirmModal}
							>Send Email to All Customers</button>
						{:else}
							<div class="feature-locked-message">
								<p>📧 Want to send marketing blasts to all your customers?</p>
								<p>This feature is available on our <strong>Premium plan</strong>.</p>
								<button class="btn btn-secondary mt-3" on:click={navigateToPayment}>Upgrade to Premium</button>
							</div>
						{/if}
			</div>
		{/if}
	</div>

	<!-- Store Branding Section -->
	<div class="admin-section">
		<div class="admin-section-header"
			on:click={() => toggleSection('branding')}
			on:keydown={(e) => handleKeydownAsClick(e, () => toggleSection('branding'))}
			role="button" tabindex="0">
			<h3>
				<svg xmlns="http://www.w3.org/2000/svg" class="section-icon" viewBox="0 0 20 20" fill="currentColor">
					<path fill-rule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0l-4.9 4.9A2 2 0 005.757 13H11v-1.757zM16 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
				</svg>
				My Store Branding
				{#if paymentInfo.selectedTier === 'basic'}
					<span class="badge badge-secondary" style="margin-left: 10px; font-size: 0.7rem; background-color: var(--secondary); color: white; padding: 0.2em 0.6em; border-radius: 10px;">Premium Feature</span>
				{/if}
			</h3>
			<span class="toggle-icon">{expandedSection === 'branding' ? '−' : '+'}</span>
		</div>

		{#if expandedSection === 'branding'}
			<div class="admin-section-content">
				{#if paymentInfo.selectedTier === 'premium'}
					<p class="mb-4">Customize your store's appearance and branding. These settings will be applied to your emails and dashboard.</p>

					<div class="admin-card mb-4">
						<div class="branding-toggle-row">
							<div>
								<h4 class="mb-1">Enable Custom Branding</h4>
								<small>Use your business name and colours in emails sent to your customers.</small>
							</div>
							<label class="toggle-switch">
								<input
									id="useCustomBranding"
									type="checkbox"
									bind:checked={storeBranding.useCustomBranding}
									on:change={() => {
										if (!storeBranding.useCustomBranding) {
											resetToDefaultColors();
										} else {
											applyCustomColors(storeBranding.primaryColor, storeBranding.secondaryColor);
										}
									}}
								/>
								<span class="toggle-slider"></span>
							</label>
						</div>
					</div>

					{#if storeBranding.useCustomBranding}
						<div class="admin-card mb-4">
							<h4 class="mb-3">Button Colors</h4>
							<p class="text-sm text-gray-400 mb-3">Customize the colors of buttons throughout your dashboard.</p>
							
							<div class="form-group">
								<label class="form-label">Primary Button Color</label>
								<div class="color-picker-row">
									<div 
										class="color-preview" 
										style="background-color: {storeBranding.primaryColor}"
										on:click={() => openColorPicker('primary')}
										on:keydown={(e) => handleKeydownAsClick(e, () => openColorPicker('primary'))}
										role="button"
										tabindex="0"
									></div>
									<input
										type="text"
										class="form-input dark-input color-input"
										bind:value={storeBranding.primaryColor}
										placeholder="#30d5c8"
										maxlength="7"
										on:input={() => applyCustomColors(storeBranding.primaryColor, storeBranding.secondaryColor)}
									/>
									<button class="btn btn-secondary btn-sm" on:click={() => openColorPicker('primary')}>Pick Color</button>
								</div>
								<small>Used for primary action buttons (Add, Save, etc.)</small>
							</div>

							<div class="form-group mt-3">
								<label class="form-label">Secondary Button Color</label>
								<div class="color-picker-row">
									<div 
										class="color-preview" 
										style="background-color: {storeBranding.secondaryColor}"
										on:click={() => openColorPicker('secondary')}
										on:keydown={(e) => handleKeydownAsClick(e, () => openColorPicker('secondary'))}
										role="button"
										tabindex="0"
									></div>
									<input
										type="text"
										class="form-input dark-input color-input"
										bind:value={storeBranding.secondaryColor}
										placeholder="#fc9403"
										maxlength="7"
										on:input={() => applyCustomColors(storeBranding.primaryColor, storeBranding.secondaryColor)}
									/>
									<button class="btn btn-secondary btn-sm" on:click={() => openColorPicker('secondary')}>Pick Color</button>
								</div>
								<small>Used for secondary action buttons (Cancel, Edit, etc.)</small>
							</div>
						</div>

						<div class="admin-card mb-4">
							<h4 class="mb-3">Store Logo</h4>
							<p class="text-sm text-gray-400 mb-3">Upload a transparent logo (PNG or SVG recommended) to display on your dashboard. Maximum size: 2MB.</p>
							
							{#if storeBranding.logoUrl}
								<div class="logo-preview-container mb-3">
									<div class="logo-preview-circle">
										<img src={storeBranding.logoUrl} alt="Store Logo" class="logo-preview-img" />
									</div>
									<button class="btn btn-danger btn-sm" on:click={removeLogo}>Remove Logo</button>
								</div>
							{/if}
							
							<div class="form-group">
								<label class="form-label" for="logoUpload">Upload Logo</label>
								<input
									id="logoUpload"
									type="file"
									accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
									on:change={handleLogoSelect}
									class="form-input dark-input"
								/>
							</div>
							{#if logoFile}
								<div class="mt-3">
									<p class="text-sm mb-2">Selected: {logoFile.name}</p>
									<button 
										class="btn btn-primary" 
										on:click={uploadLogo}
										disabled={isUploadingLogo}
									>
										{isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
									</button>
								</div>
							{/if}
						</div>
					{/if}

					<button class="btn btn-primary w-full" on:click={saveStoreBranding} disabled={loading}>
						{loading ? 'Saving...' : 'Save Branding Settings'}
					</button>
				{:else}
					<div class="feature-locked-message">
						<p>🎨 Want to customize your store branding?</p>
						<p>This feature allows you to:</p>
						<ul style="text-align: left; margin: 1rem 0; padding-left: 1.5rem;">
							<li>Add your company name to customer emails</li>
							<li>Choose between light and dark mode</li>
							<li>Upload your store logo</li>
						</ul>
						<p>Available on our <strong>Premium plan</strong>.</p>
						<button class="btn btn-secondary mt-3" on:click={navigateToPayment}>Upgrade to Premium</button>
					</div>
				{/if}
			</div>
		{/if}
	</div>

	<!-- Employee Management Section -->
	<div class="admin-section">
		<div class="admin-section-header"
			on:click={() => toggleSection('employees')}
			on:keydown={(e) => handleKeydownAsClick(e, () => toggleSection('employees'))}
			role="button" tabindex="0">
			<h3>
				<svg xmlns="http://www.w3.org/2000/svg" class="section-icon" viewBox="0 0 20 20" fill="currentColor">
					<path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
				</svg>
				Manage Employees
			</h3>
			<span class="toggle-icon">{expandedSection === 'employees' ? '−' : '+'}</span>
		</div>

		{#if expandedSection === 'employees'}
			<div class="admin-section-content">
				<p class="mb-4">Manage employee accounts and their permissions for accessing different parts of the system.</p>
				
				<div class="admin-card mb-4">
					<div class="flex justify-between items-center mb-3">
						<h4>Employee List</h4>
						<button class="btn btn-primary" on:click={openAddEmployeeModal}>Add Employee</button>
					</div>
					
					{#if employees.length === 0}
						<p>No employees added yet. Click "Add Employee" to begin.</p>
					{:else}
						<div class="employee-list">
							{#each employees as employee (employee.id)}
								<div class="employee-item">
									<div class="employee-details">
										<div class="employee-name">{employee.name}</div>
										<div class="employee-email">{employee.email}</div>
										<div class="employee-permissions">
											{#if employee.permissions.full_access}
												<span class="permission-badge full-access">Full Access</span>
											{:else}
												{#if employee.permissions.view_customers}<span class="permission-badge">View Customers</span>{/if}
												{#if employee.permissions.add_customers}<span class="permission-badge">Add Customers</span>{/if}
												{#if employee.permissions.delete_customers}<span class="permission-badge">Delete Customers</span>{/if}
												{#if employee.permissions.points_history}<span class="permission-badge">Points History</span>{/if}
												{#if employee.permissions.marketing_messages}<span class="permission-badge">Marketing</span>{/if}
											{/if}
										</div>
									</div>
									<div class="employee-actions">
										<button class="btn btn-secondary btn-sm" on:click={() => openEditEmployeeModal(employee)}>Edit</button>
										<button class="btn btn-danger btn-sm" on:click={() => deleteEmployee(employee.id)}>Delete</button>
									</div>
								</div>
							{/each}
						</div>
					{/if}
				</div>
			</div>
		{/if}
	</div>
</div>
{/if}

{#if currentView === 'customers'}
		<div class="card dark-card">
			<div class="flex justify-between items-center mb-4">
				<h2>Customers</h2>
				<button class="btn btn-primary" on:click={openAddCustomerModal}>Add Customer</button>
			</div>

			<div class="search-container">
				<div class="search-input-wrapper">
					<input
						type="text"
						class="form-input dark-input search-input"
						placeholder="Search by name, email or phone..."
						bind:value={searchQuery}
						on:input={handleSearch}
					/>
					<button class="search-button" on:click={handleSearch} aria-label="Search">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style="width:20px; height:20px;">
							<path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"></path>
						</svg>
					</button>
					{#if searchQuery}
						<button class="search-clear" on:click={() => { searchQuery = ''; handleSearch(); }} aria-label="Clear search">×</button>
					{/if}
				</div>
			</div>

			<div class="flex-wrap">
				{#if loading && customers.length === 0}
					<p>Loading customers...</p>
				{:else if filteredCustomers.length === 0}
					{#if searchQuery}
						<p>No customers match your search criteria "{searchQuery}".</p>
					{:else}
						<p>No customers added yet. Click "Add Customer" to begin.</p>
					{/if}
				{:else}
					{#each filteredCustomers as customer (customer.id)}
						<div class="customer-card">
							<div class="flex justify-between items-center mb-2">
								<h4>{obfuscateName(customer.name)}</h4>
								<span class="badge {calculateFreebies(customer.points) > 0 ? 'badge-secondary' : 'badge-primary'}">
									{#if calculateFreebies(customer.points) > 0}
										({calculateFreebies(customer.points)}) Freebie{calculateFreebies(customer.points) > 1 ? 's' : ''}
									{:else}
										{customer.points} / {minPointsNeeded}
									{/if}
								</span>
							</div>

							<div class="mb-2 customer-info">
								{#if customer.email}<small>{customer.email}</small>{/if}
								{#if customer.email && customer.cell}<br>{/if}
								{#if customer.cell}<small>{obfuscateCell(customer.cell)}</small>{/if}
							</div>

							<div class="progress-container">
								<div class="progress-bar" style="width: {calculateProgress(customer.points)}%"></div>
								<div class="progress-text">{customer.points % minPointsNeeded} / {minPointsNeeded}</div>
							</div>

							{#if calculateFreebies(customer.points) > 0}
								<button class="btn btn-secondary w-full mb-2" on:click={() => openRedeemModal(customer)}>Redeem Freebie</button>
							{/if}

							<div class="flex gap-2 mt-3">
								<button class="btn btn-primary flex-grow" on:click={() => addPoint(customer)}>Add Point</button>
								<button class="btn btn-edit flex-grow" on:click={() => openEditCustomerModal(customer)}>Edit</button>
							</div>
						</div>
					{/each}
				{/if}
			</div>
		</div>
	{/if}

	{#if showCustomerModal}
		<div class="modal-backdrop">
			<div class="modal dark-modal customer-modal" role="dialog" aria-modal="true" aria-labelledby="customerModalTitle">
				<div class="modal-header">
					<h3 class="modal-title" id="customerModalTitle">
						{#if customerModalMode === 'add'}
							Add New Customer
						{:else if customerModalMode === 'edit'}
							Edit Customer {selectedCustomer ? `- ${selectedCustomer.name}` : ''}
						{:else}
							Customer Details {selectedCustomer ? `- ${selectedCustomer.name}` : ''}
						{/if}
					</h3>
					<button class="modal-close" on:click={closeCustomerModal} aria-label="Close modal">×</button>
				</div>

				<div class="modal-body">
					{#if customerModalMode === 'view'}
						{#if selectedCustomer}
							<div>
								<h4>{selectedCustomer.name}</h4>
								<p>Email: {selectedCustomer.email || 'N/A'}</p>
								<p>Phone: {selectedCustomer.cell || 'N/A'}</p>
								<p>Points: {selectedCustomer.points} / {minPointsNeeded}</p>
								<p>Preferred Contact:
									{#if parsePreferredContact(selectedCustomer).email}Email {/if}
									{#if parsePreferredContact(selectedCustomer).cell}Cell{/if}
								</p>
								
								<div class="progress-container mt-3">
									<div class="progress-bar" style="width: {calculateProgress(selectedCustomer.points)}%"></div>
									<div class="progress-text">{selectedCustomer.points % minPointsNeeded} / {minPointsNeeded}</div>
								</div>

								<div class="mt-4">
									<button class="btn btn-secondary" on:click={closeCustomerModal}>Close</button>
								</div>
							</div>
						{:else}
							<p>No customer selected.</p>
						{/if}
					{:else}
						<form on:submit|preventDefault={saveCustomer}>
							<div class="form-group compact">
								<label class="form-label" for="customerName">Name</label>
								<input
									id="customerName"
									type="text"
									class="form-input dark-input"
									bind:value={newCustomer.name}
									required
								/>
							</div>

							<div class="form-group compact">
								<label class="form-label" for="customerEmail">Email <span class="text-muted" style="font-weight:normal;">(Email or Phone required)</span></label>
								<input
									id="customerEmail"
									type="email"
									class="form-input dark-input"
									bind:value={newCustomer.email}
								/>
							</div>

							<div class="form-group compact">
								<label class="form-label" for="customerCell">Phone Number <span class="text-muted" style="font-weight:normal;">(Email or Phone required)</span></label>
								<input
									id="customerCell"
									type="tel"
									class="form-input dark-input"
									bind:value={newCustomer.cell}
								/>
							</div>
							
							<fieldset class="form-group compact">
								<legend class="form-label">Preferred Contact Method</legend>
								<div class="contact-options">
									<label class="contact-option" for="preferEmail">
										<input
											type="checkbox"
											bind:checked={preferredContactOptions.email}
											id="preferEmail"
										/>
										Email
									</label>
									<label class="contact-option" for="preferCell">
										<input
											type="checkbox"
											bind:checked={preferredContactOptions.cell}
											id="preferCell"
										/>
										Cell Phone
									</label>
								</div>
							</fieldset>
							
							{#if customerModalMode === 'edit'}
								<div class="form-group compact">
									<label class="form-label" for="customerPoints">Points <span class="text-muted" style="font-weight:normal;">(use Add Point button to adjust)</span></label>
									<input
										id="customerPoints"
										type="number"
										class="form-input dark-input"
										value={newCustomer.points}
										readonly
										style="opacity: 0.6; cursor: not-allowed;"
									/>
								</div>
							{/if}

							<div class="flex gap-2 mt-3">
								{#if customerModalMode === 'edit' && selectedCustomer}
									<button
										type="button"
										class="btn btn-danger"
										on:click={() => {
											if (confirm(`Are you sure you want to delete ${selectedCustomer.name}? This action cannot be undone.`)) {
												deleteCustomer(selectedCustomer.id);
											}
										}}
										disabled={loading}
									>
										Delete
									</button>
								{/if}
								<button type="button" class="btn btn-secondary" on:click={closeCustomerModal}>Cancel</button>
								<button
									type="submit"
									class="btn btn-primary"
									disabled={loading || !newCustomer.name || (!newCustomer.email && !newCustomer.cell)}
								>
									{loading ? 'Saving...' : 'Save Customer'}
								</button>
							</div>
						</form>
					{/if}
				</div>
			</div>
		</div>
	{/if}

	{#if showRedeemModal}
		<div class="modal-backdrop">
			<div class="modal dark-modal" role="dialog" aria-modal="true" aria-labelledby="redeemModalTitle">
				<div class="modal-header">
					<h3 class="modal-title" id="redeemModalTitle">Redeem Freebie</h3>
					<button class="modal-close" on:click={closeRedeemModal} aria-label="Close modal">×</button>
				</div>

				<div class="modal-body">
					{#if selectedCustomer && products}
						<p>Customer: <strong>{selectedCustomer.name}</strong></p>
						<p>Available Points: <strong>{selectedCustomer.points}</strong></p>

						<div class="form-group mt-3">
							<label class="form-label" for="redeemService">Select Service to Redeem</label>

							<div class="redeem-product-list">
								{#each products as product (product.id)}
									{#if product.points_needed > 0}
										<div
											class="redeem-product-item {selectedCustomer.points < product.points_needed ? 'disabled' : ''}"
											class:selected={selectedService && selectedService.id === product.id}
											on:click={() => { if (selectedCustomer.points >= product.points_needed) selectService(product); }}
											on:keydown={(e) => { if (e.key === 'Enter' && selectedCustomer.points >= product.points_needed) selectService(product); }}
											role="button"
											tabindex={selectedCustomer.points >= product.points_needed ? 0 : -1}
											aria-disabled={selectedCustomer.points < product.points_needed}
										>
											<div class="product-info">
												<h4>{product.name}</h4>
												<div class="points-required">{product.points_needed} points</div>
											</div>

											{#if selectedCustomer.points < product.points_needed}
												<div class="not-available-tag">Not enough points</div>
											{:else if selectedService && selectedService.id === product.id}
												<div class="selected-tag">Selected</div>
											{/if}
										</div>
									{/if}
								{/each}
								{#if products.filter(p => p.points_needed > 0 && selectedCustomer.points >= p.points_needed).length === 0}
									<p>No products currently redeemable with {selectedCustomer.points} points.</p>
								{/if}
							</div>
						</div>

						<div class="flex gap-2 mt-4">
							<button class="btn btn-secondary" on:click={closeRedeemModal}>Cancel</button>
							<button
								class="btn btn-success"
								on:click={redeemFreebie}
								disabled={!selectedService || selectedCustomer.points < (selectedService?.points_needed || Infinity)}
							>
								{loading ? 'Processing...' : 'Confirm Redemption'}
							</button>
						</div>
					{:else}
						<p>Loading redemption details...</p>
					{/if}
				</div>
			</div>
		</div>
	{/if}

	{#if emailConfirmModalVisible}
		<div class="modal-backdrop">
			<div class="modal dark-modal" role="dialog" aria-modal="true" aria-labelledby="emailConfirmModalTitle">
				<div class="modal-header">
					<h3 class="modal-title" id="emailConfirmModalTitle">Confirm Email Marketing</h3>
					<button class="modal-close" on:click={closeEmailConfirmModal} aria-label="Close modal">×</button>
				</div>

				<div class="modal-body">
					<p class="mb-3">You are about to send promotional emails to <strong>{eligibleEmailCount}</strong> customers with valid email addresses who have opted to receive emails.</p>

					<div class="email-preview mb-3">
						<h4 class="mb-2">Message Preview:</h4>
						<div class="email-preview-content">
							{blastMessage}
						</div>
					</div>

					<div class="flex gap-2 mt-4">
						<button class="btn btn-secondary" on:click={closeEmailConfirmModal} disabled={loading}>Cancel</button>
						<button class="btn btn-primary" on:click={sendMarketingEmails} disabled={loading}>
							{loading ? 'Sending...' : 'Send Emails'}
						</button>
					</div>
				</div>
			</div>
		</div>
	{/if}

	{#if showForgotPasswordModal}
		<div class="modal-backdrop">
			<div class="modal dark-modal" role="dialog" aria-modal="true" aria-labelledby="forgotPasswordModalTitle">
				<div class="modal-header">
					<h3 class="modal-title" id="forgotPasswordModalTitle">Reset Password</h3>
					<button class="modal-close" on:click={closeForgotPasswordModal} aria-label="Close modal">×</button>
				</div>

				<div class="modal-body">
					<p class="mb-3">Enter your email address and your new password. We'll send you a link to confirm the password change.</p>

					<form on:submit|preventDefault={sendPasswordResetLink}>
						<div class="form-group">
							<label class="form-label" for="resetEmail">Email Address</label>
							<input
								id="resetEmail"
								type="email"
								class="form-input dark-input"
								bind:value={forgotPasswordEmail}
								required
							/>
						</div>
						<div class="form-group">
							<label class="form-label" for="resetNewPassword">New Password</label>
							<div class="password-input-container">
								{#if showForgotPasswordNew}
									<input
										id="resetNewPassword"
										type="text"
										class="form-input dark-input"
										bind:value={forgotPasswordNewPassword}
										placeholder="Enter your new password (min 8 characters)"
										minlength="8"
										required
									/>
								{:else}
									<input
										id="resetNewPassword"
										type="password"
										class="form-input dark-input"
										bind:value={forgotPasswordNewPassword}
										placeholder="Enter your new password (min 8 characters)"
										minlength="8"
										required
									/>
								{/if}
								<button
									type="button"
									class="password-toggle"
									on:click={() => showForgotPasswordNew = !showForgotPasswordNew}
									aria-label={showForgotPasswordNew ? 'Hide password' : 'Show password'}
								>
									{#if showForgotPasswordNew}
										<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-2.891 0-5.729-1.295-7.841-3.821C2.947 13.635 2 11.473 2 10c0-1.473.947-3.635 2.159-5.179a10.099 10.099 0 013.455-2.849m10.88 2.849C19.753 6.365 20.7 8.527 20.7 10c0 1.473-.947 3.635-2.159 5.179C16.729 17.705 13.891 19 11 19c-.498 0-.991-.05-1.472-.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L16 7.5 14.5 6l-4.5 4.5a3 3 0 00-2.121 5.121L3 21l1.5 1.5 5.379-5.379z" />
										</svg>
									{:else}
										<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
										</svg>
									{/if}
								</button>
							</div>
						</div>
						<div class="form-group">
							<label class="form-label" for="resetConfirmPassword">Confirm Password</label>
							<div class="password-input-container">
								{#if showForgotPasswordConfirm}
									<input
										id="resetConfirmPassword"
										type="text"
										class="form-input dark-input"
										bind:value={forgotPasswordConfirmPassword}
										placeholder="Confirm your new password"
										minlength="8"
										required
									/>
								{:else}
									<input
										id="resetConfirmPassword"
										type="password"
										class="form-input dark-input"
										bind:value={forgotPasswordConfirmPassword}
										placeholder="Confirm your new password"
										minlength="8"
										required
									/>
								{/if}
								<button
									type="button"
									class="password-toggle"
									on:click={() => showForgotPasswordConfirm = !showForgotPasswordConfirm}
									aria-label={showForgotPasswordConfirm ? 'Hide password' : 'Show password'}
								>
									{#if showForgotPasswordConfirm}
										<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-2.891 0-5.729-1.295-7.841-3.821C2.947 13.635 2 11.473 2 10c0-1.473.947-3.635 2.159-5.179a10.099 10.099 0 013.455-2.849m10.88 2.849C19.753 6.365 20.7 8.527 20.7 10c0 1.473-.947 3.635-2.159 5.179C16.729 17.705 13.891 19 11 19c-.498 0-.991-.05-1.472-.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L16 7.5 14.5 6l-4.5 4.5a3 3 0 00-2.121 5.121L3 21l1.5 1.5 5.379-5.379z" />
										</svg>
									{:else}
										<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="eye-icon">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
										</svg>
									{/if}
								</button>
							</div>
						</div>
						<div class="flex gap-2 mt-4">
							<button type="button" class="btn btn-secondary" on:click={closeForgotPasswordModal} disabled={loading}>Cancel</button>
							<button type="submit" class="btn btn-primary" disabled={loading}>
								{loading ? 'Sending...' : 'Send Reset Link'}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	{/if}

	{#if showEmployeeModal}
		<div class="modal-backdrop">
			<div class="modal dark-modal employee-modal" role="dialog" aria-modal="true" aria-labelledby="employeeModalTitle">
				<div class="modal-header">
					<h3 class="modal-title" id="employeeModalTitle">
						{#if employeeModalMode === 'add'}
							Add New Employee
						{:else if employeeModalMode === 'edit'}
							Edit Employee {selectedEmployee ? `- ${selectedEmployee.name}` : ''}
						{/if}
					</h3>
					<button class="modal-close" on:click={closeEmployeeModal} aria-label="Close modal">×</button>
				</div>

				<div class="modal-body">
					<form on:submit|preventDefault={saveEmployee}>
						<div class="form-group">
							<label class="form-label" for="employeeName">Full Name</label>
							<input
								id="employeeName"
								type="text"
								class="form-input dark-input"
								bind:value={newEmployee.name}
								placeholder="Enter employee's full name"
								required
							/>
						</div>

						<div class="form-group">
							<label class="form-label" for="employeeEmail">Email Address</label>
							<input
								id="employeeEmail"
								type="email"
								class="form-input dark-input"
								bind:value={newEmployee.email}
								placeholder="Enter employee's email address"
								required
							/>
						</div>

						<div class="form-group">
							<label class="form-label" for="employeePassword">Password {employeeModalMode === 'edit' ? '(leave blank to keep current)' : ''}</label>
							<input
								id="employeePassword"
								type="password"
								class="form-input dark-input"
								bind:value={newEmployee.password}
								placeholder="Enter password"
								required={employeeModalMode === 'add'}
							/>
						</div>

						<fieldset class="form-group">
							<legend class="form-label">Permissions</legend>
							<div class="permissions-list">
								<p class="text-sm text-gray-400 mb-2">All employees can view and add customers by default</p>
								
								<label class="permission-option full-access-option">
									<input
										type="checkbox"
										bind:checked={newEmployee.permissions.full_access}
										on:change={() => {
											if (newEmployee.permissions.full_access) {
												newEmployee.permissions = {
													delete_customers: true,
													edit_products: true,
													points_history: true,
													marketing_messages: true,
													full_access: true
												};
											}
										}}
									/>
									<span class="permission-label full-access-label">Full Access (All Permissions)</span>
								</label>

								<label class="permission-option">
									<input
										type="checkbox"
										bind:checked={newEmployee.permissions.delete_customers}
										disabled={newEmployee.permissions.full_access}
									/>
									<span class="permission-label">Delete Customers</span>
								</label>

								<label class="permission-option">
									<input
										type="checkbox"
										bind:checked={newEmployee.permissions.edit_products}
										disabled={newEmployee.permissions.full_access}
									/>
									<span class="permission-label">Edit Products</span>
								</label>

								<label class="permission-option">
									<input
										type="checkbox"
										bind:checked={newEmployee.permissions.points_history}
										disabled={newEmployee.permissions.full_access}
									/>
									<span class="permission-label">View Points History</span>
								</label>

								<label class="permission-option">
									<input
										type="checkbox"
										bind:checked={newEmployee.permissions.marketing_messages}
										disabled={newEmployee.permissions.full_access}
									/>
									<span class="permission-label">Send Marketing Messages</span>
								</label>
							</div>
						</fieldset>

						<div class="flex gap-2 mt-4">
							{#if employeeModalMode === 'edit'}
								<button
									type="button"
									class="btn btn-danger"
									on:click={() => {
										if (confirm(`Are you sure you want to delete ${selectedEmployee.name}? This action cannot be undone.`)) {
											deleteEmployee(selectedEmployee.id);
										}
									}}
									disabled={loading}
								>
									Delete
								</button>
							{/if}
							<button type="button" class="btn btn-secondary" on:click={closeEmployeeModal}>Cancel</button>
							<button
								type="submit"
								class="btn btn-primary"
								disabled={loading || !newEmployee.name || !newEmployee.email || (employeeModalMode === 'add' && !newEmployee.password)}
							>
								{loading ? 'Saving...' : employeeModalMode === 'add' ? 'Add Employee' : 'Update Employee'}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	{/if}

	{#if currentView === 'reset-password'}
		<div class="card dark-card max-w-md mx-auto">
			<h2 class="text-center mb-4">Reset Your Password</h2>

			{#if resetPasswordState === 'loading'}
				<div class="text-center mb-4">
					<div class="spinner mb-2"></div>
					<p>Verifying your reset token...</p>
				</div>
			{:else if resetPasswordState === 'invalid'}
				<div class="text-center mb-4">
					<p class="alert alert-danger" role="alert">{alert.message || 'Invalid or expired password reset token.'}</p>
					<p>Please request a new password reset link.</p>
					<button class="btn-link mt-3" on:click={() => { currentView = 'login'; resetPasswordState = 'loading'; }}>
						Return to Login
					</button>
				</div>
			{:else if resetPasswordState === 'success'}
				<div class="text-center mb-4">
					<p class="alert alert-success" role="alert">{alert.message || 'Your password has been reset successfully!'}</p>
					<p>You can now log in with your new password.</p>
					<button class="btn-link mt-3" on:click={() => { currentView = 'login'; resetPasswordState = 'loading'; }}>
						Return to Login
					</button>
				</div>
			{/if}
		</div>
	{/if}

	{#if showColorPickerModal}
		<div class="modal-backdrop">
			<div class="modal dark-modal color-picker-modal" role="dialog" aria-modal="true" aria-labelledby="colorPickerTitle">
				<div class="modal-header">
					<h3 class="modal-title" id="colorPickerTitle">
						{colorPickerTarget === 'primary' ? 'Choose Primary Color' : 'Choose Secondary Color'}
					</h3>
					<button class="modal-close" on:click={closeColorPicker} aria-label="Close modal">×</button>
				</div>

				<div class="modal-body">
					<p class="mb-3 text-center">Pick a color from the palette below:</p>
					
					<!-- Color Palette Grid -->
					<div class="color-palette">
						{#each [
							'#1a365d', '#2c5282', '#2b6cb0', '#3182ce', '#4299e1', '#63b3ed', '#90cdf4', '#bee3f8',
							'#744210', '#975a16', '#b7791f', '#d69e2e', '#ecc94b', '#f6e05e', '#faf089', '#fefcbf',
							'#22543d', '#276749', '#2f855a', '#38a169', '#48bb78', '#68d391', '#9ae6b4', '#c6f6d5',
							'#822727', '#9b2c2c', '#c53030', '#e53e3e', '#fc8181', '#feb2b2', '#fed7d7', '#fff5f5',
							'#702459', '#97266d', '#b83280', '#d53f8c', '#ed64a6', '#f687b3', '#fbb6ce', '#fed7e2',
							'#44337a', '#553c9a', '#6b46c1', '#805ad5', '#9f7aea', '#b794f4', '#d6bcfa', '#e9d8fd',
							'#2d3748', '#4a5568', '#718096', '#a0aec0', '#cbd5e0', '#e2e8f0', '#edf2f7', '#f7fafc',
							'#000000', '#1a202c', '#2d3748', '#4a5568', '#718096', '#a0aec0', '#cbd5e0', '#ffffff'
						] as color}
							<button
								class="color-swatch"
								style="background-color: {color}"
								on:click={() => selectColor(color)}
								aria-label="Select color {color}"
							></button>
						{/each}
					</div>

					<div class="color-input-row mt-4">
						<label class="form-label" for="customColor">Or enter a hex color:</label>
						<div class="flex gap-2">
							<input
								id="customColor"
								type="text"
								class="form-input dark-input"
								placeholder="#30d5c8"
								maxlength="7"
								value={colorPickerTarget === 'primary' ? storeBranding.primaryColor : storeBranding.secondaryColor}
								on:input={(e) => selectColor(e.target.value)}
							/>
							<button class="btn btn-primary" on:click={closeColorPicker}>OK</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	{/if}

	<!-- Footer with Loyalty Pro branding -->
	{#if isAuthenticated}
		<footer class="app-footer">
			<div class="footer-content">
				<span class="footer-branding">Powered by <strong>Loyalty Pro</strong></span>
			</div>
		</footer>
	{/if}
</div>

<style>
	:global(:root) {
		--primary: #30d5c8;
		/* Turquoise */
		--secondary: #FF9F1C;
		/* Orange */
		--dark-blue: #1e2231;
		--dark-card: #262b3f;
		--bg-darker: #1c2036;
		/* Even darker background for nested elements */
		--text-light: #ffffff;
		--text-muted: #8d97b0;
		--text-dark: #1A202C;
		--border-radius: 50px;
		/* Pill shape */
		--border-radius-sm: 12px;
		--shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		--border-color: rgba(255, 255, 255, 0.1);
		--danger: #E53E3E;
		--success: #38A169;
	}

	:global(*) {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
		font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
	}

	:global(body) {
		background-color: var(--dark-blue);
		color: var(--text-light);
		line-height: 1.6;
	}

	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 1rem;
		background-color: var(--dark-blue);
	}

	.dark-card {
		background-color: var(--dark-card);
		color: var(--text-light);
		border: 1px solid var(--border-color);
	}

	.dark-input {
		background-color: #2A3146;
		color: var(--text-light);
		border: 1px solid var(--border-color);
	}

	.dark-modal {
		background-color: var(--dark-card);
		color: var(--text-light);
	}

	/* Components */
	:global(.card) {
		background: var(--dark-blue);
		border-radius: var(--border-radius-sm);
		box-shadow: var(--shadow);
		padding: 1.5rem;
		margin-bottom: 1.5rem;
		transition: transform 0.3s ease;
	}

	:global(.customer-card) {
		background-color: var(--dark-card);
		color: var(--text-light);
		border-radius: var(--border-radius-sm);
		padding: 1.2rem;
		margin-bottom: 1rem;
		border: 1px solid var(--border-color);
		transition: transform 0.3s ease;
		max-width: 360px;
		width: 100%;
	}

	:global(.customer-card:hover) {
		transform: translateY(-3px);
	}

	/* Customer card container */
	.flex-wrap {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		justify-content: flex-start;
	}

	.customer-info {
		color: var(--text-muted);
	}

	/* Admin redesign styles */
	.admin-section {
		margin-bottom: 1.5rem;
		border: 1px solid var(--border-color);
		border-radius: var(--border-radius-sm);
		overflow: hidden;
		background-color: var(--bg-darker);
	}

	.admin-section-header {
		padding: 1rem;
		background-color: var(--dark-card);
		display: flex;
		justify-content: space-between;
		align-items: center;
		cursor: pointer;
		transition: background-color 0.2s;
		color: var(--text-light);
	}

	.admin-section-header:hover {
		background-color: var(--dark-blue);
	}

	.admin-section-header h3 {
		display: flex;
		align-items: center;
		margin: 0;
		font-size: 1.1rem;
		color: var(--text-light);
	}

	.section-icon {
		width: 1.25rem;
		height: 1.25rem;
		margin-right: 0.75rem;
		color: var(--primary);
	}

	.toggle-icon {
		font-size: 1.5rem;
		font-weight: bold;
		color: var(--primary);
	}

	.admin-section-content {
		padding: 1rem;
		border-top: 1px solid var(--border-color);
		color: var(--text-light);
	}

	.admin-card {
		background-color: var(--dark-card);
		border-radius: var(--border-radius-sm);
		padding: 1rem;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		color: var(--text-light);
	}

	.product-list {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.product-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.75rem;
		border-radius: var(--border-radius-sm);
		background-color: var(--bg-darker);
		border: 1px solid var(--border-color);
	}

	.product-details {
		display: flex;
		flex-direction: column;
		color: var(--text-light);
	}

	/* Feature Locked - Upgrade Package*/
	.feature-locked-message {
		background-color: rgba(255, 255, 255, 0.05);
		border: 1px dashed var(--border-color);
		padding: 1rem;
		border-radius: var(--border-radius-sm);
		text-align: center;
		margin-top: 1rem;
	}

	.feature-locked-message p {
		margin-bottom: 0.5rem;
		color: var(--text-light);
	}

	/* Points history table */
	.history-table-container {
		width: 100%;
		overflow-x: auto;
	}

	.history-table {
		width: 100%;
		border-collapse: collapse;
		min-width: 600px;
	}

	.history-table th,
	.history-table td {
		padding: 0.75rem;
		text-align: left;
		border-bottom: 1px solid var(--border-color);
	}

	.history-table th {
		font-weight: 600;
		background-color: var(--dark-blue);
	}

	.history-table tr:hover {
		background-color: var(--bg-darker);
	}

	/* Email Preview */
	.email-preview {
		background-color: var(--bg-darker);
		border: 1px solid var(--border-color);
		border-radius: var(--border-radius-sm);
		padding: 1rem;
	}

	.email-preview-content {
		background-color: white;
		color: var(--text-dark);
		padding: 1rem;
		border-radius: var(--border-radius-sm);
		white-space: pre-wrap;
		font-family: Arial, sans-serif;
	}

	/* Payment status card */
	.payment-status-card {
		display: flex;
		padding: 1rem;
		border-radius: var(--border-radius-sm);
		align-items: center;
		gap: 1rem;
		background-color: var(--bg-darker);
		border: 1px solid var(--border-color);
	}

	.payment-status-free {
		border-left: 4px solid #4299e1;
	}

	.payment-status-due {
		border-left: 4px solid #fc8181;
	}

	.payment-status-paid {
		border-left: 4px solid #68d391;
	}

	.payment-status-icon {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.25rem;
		font-weight: bold;
	}

	.payment-status-free .payment-status-icon {
		background-color: #4299e1;
		color: white;
	}

	.payment-status-due .payment-status-icon {
		background-color: #fc8181;
		color: white;
	}

	.payment-status-paid .payment-status-icon {
		background-color: #68d391;
		color: white;
	}

	.payment-status-content {
		flex: 1;
		color: var(--text-light);
	}

	.payment-status-content h5 {
		margin-bottom: 0.5rem;
		font-weight: 600;
	}

	/* Password input container */
	.password-input-container {
		position: relative;
	}

	.password-toggle {
		position: absolute;
		right: 0.75rem;
		top: 50%;
		transform: translateY(-50%);
		background: none;
		border: none;
		color: #718096;
		cursor: pointer;
		padding: 0.25rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.eye-icon {
		width: 1.25rem;
		height: 1.25rem;
	}

	:global(.add-button) {
		padding: 0.5rem 1rem;
		border-radius: var(--border-radius);
		font-size: 0.9rem;
	}

	:global(.btn) {
		display: inline-block;
		padding: 0.5rem 1.5rem;
		border-radius: var(--border-radius);
		border: none;
		cursor: pointer;
		font-weight: 500;
		text-align: center;
		transition: all 0.3s ease;
	}

	:global(.btn-primary) {
		background-color: var(--primary);
		color: white;
	}

	:global(.btn-primary:hover) {
		opacity: 0.9;
		transform: translateY(-2px);
	}

	:global(.btn-secondary) {
		background-color: var(--secondary);
		color: white;
	}

	:global(.btn-secondary:hover) {
		opacity: 0.9;
		transform: translateY(-2px);
	}

	:global(.btn-edit) {
		background-color: rgba(48, 213, 200, 0.2);
		color: white;
	}

	:global(.btn-edit:hover) {
		opacity: 0.9;
		transform: translateY(-2px);
	}

	:global(.btn-danger) {
		background-color: var(--danger);
		color: white;
	}

	:global(.btn-success) {
		background-color: var(--success);
		color: white;
	}

	:global(.btn-sm) {
		padding: 0.25rem 0.75rem;
		font-size: 0.875rem;
	}

	:global(.btn-link) {
		background: none;
		border: none;
		color: var(--primary);
		text-decoration: underline;
		cursor: pointer;
		padding: 0;
		font: inherit;
	}

	:global(.btn-link:hover) {
		color: var(--secondary);
	}

	:global(.form-group) {
		margin-bottom: 1rem;
	}

	:global(.form-label) {
		display: block;
		margin-bottom: 0.5rem;
		font-weight: 500;
		color: var(--text-light);
	}
	:global(legend.form-label) { /* Specificity for legend */
		padding: 0; /* Reset legend padding */
	}


	:global(.form-input) {
		width: 100%;
		padding: 0.75rem 1rem;
		border-radius: var(--border-radius-sm);
		border: 1px solid #e2e8f0;
		font-size: 16px;
		transition: border 0.2s;
	}

	:global(.form-input:focus) {
		outline: none;
		border-color: var(--primary);
	}

	:global(.badge) {
		display: inline-block;
		padding: 0.25rem 0.75rem;
		border-radius: var(--border-radius);
		font-size: 0.75rem;
		font-weight: 600;
	}

	:global(.badge-primary) {
		background-color: var(--primary);
		color: white;
	}

	:global(.badge-secondary) {
		background-color: var(--secondary);
		color: white;
	}

	/* Progress Bar styles */
	.progress-container {
		position: relative;
		height: 20px;
		background-color: rgba(255, 255, 255, 0.1);
		border-radius: var(--border-radius);
		margin: 0.5rem 0 1rem;
		overflow: hidden;
	}

	.progress-bar {
		height: 100%;
		background-color: var(--primary);
		border-radius: var(--border-radius);
		transition: width 0.3s ease;
	}

	.progress-text {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-size: 0.75rem;
		font-weight: 600;
		text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
	}

	/* Mobile Improvements */
	.search-input-wrapper {
		position: relative;
		flex-grow: 1;
	}

	.search-input {
		padding-right: 80px;
	}

	.search-button {
		position: absolute;
		right: 10px;
		top: 50%;
		transform: translateY(-50%);
		background: transparent;
		border: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		padding: 0;
		color: #718096;
	}

	.search-button svg {
		width: 20px;
		height: 20px;
	}

	.search-clear {
		position: absolute;
		right: 40px;
		top: 50%;
		transform: translateY(-50%);
		background: none;
		border: none;
		color: #718096;
		font-size: 1.5rem;
		cursor: pointer;
		padding: 0;
		line-height: 1;
	}

	.search-container {
		margin-bottom: 1.5rem;
		display: flex;
		gap: 0.5rem;
	}

	/* Loading indicator */
	.loading-indicator {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		margin-bottom: 1rem;
		color: var(--text-light);
	}

	.spinner {
		width: 30px;
		height: 30px;
		border-radius: 50%;
		border: 3px solid rgba(48, 213, 200, 0.2);
		border-top-color: var(--primary);
		animation: spin 1s linear infinite;
		margin-bottom: 0.5rem;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	/* Desktop vs Mobile Navigation */
	.desktop-nav {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1rem 0;
		margin-bottom: 1rem;
	}

	.mobile-nav {
		display: none;
	}

	/* Redemption product list styles */
	.redeem-product-list {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		max-height: 300px;
		overflow-y: auto;
		margin-top: 0.5rem;
	}

	.redeem-product-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.75rem;
		border-radius: var(--border-radius-sm);
		background-color: var(--bg-darker);
		border: 1px solid var(--border-color);
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.redeem-product-item:hover:not(.disabled) {
		transform: translateY(-2px);
		box-shadow: var(--shadow);
	}

	.redeem-product-item.selected {
		border-color: var(--primary);
		background-color: rgba(48, 213, 200, 0.1);
	}

	.redeem-product-item.disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background-color: rgba(0, 0, 0, 0.1);
	}

	.product-info h4 {
		margin: 0;
		font-size: 1rem;
	}

	.points-required {
		font-size: 0.8rem;
		color: var(--text-muted);
	}

	.not-available-tag {
		font-size: 0.75rem;
		color: var(--danger);
		padding: 0.25rem 0.5rem;
		border-radius: var(--border-radius);
		background-color: rgba(229, 62, 62, 0.1);
	}

	.selected-tag {
		font-size: 0.75rem;
		color: var(--primary);
		padding: 0.25rem 0.5rem;
		border-radius: var(--border-radius);
		background-color: rgba(48, 213, 200, 0.1);
	}

	/* Subscription tiers styling */
	.subscription-tiers {
		display: flex;
		gap: 1.5rem;
		flex-wrap: wrap;
		justify-content: center;
	}

	.subscription-tier {
		flex: 1;
		min-width: 250px;
		border: 1px solid var(--border-color);
		border-radius: var(--border-radius-sm);
		padding: 1.5rem;
		position: relative;
		transition: all 0.3s ease;
		background-color: var(--dark-card);
	}

	.subscription-tier.selected {
		border-color: var(--primary);
		box-shadow: 0 0 0 1px var(--primary);
	}

	.tier-badge {
		position: absolute;
		top: -10px;
		right: 10px;
		background-color: var(--secondary);
		color: white;
		font-size: 0.7rem;
		font-weight: bold;
		padding: 0.2rem 0.6rem;
		border-radius: var(--border-radius);
	}

	.tier-header {
		text-align: center;
		margin-bottom: 1rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-color);
	}

	.tier-header h5 {
		font-size: 1.25rem;
		font-weight: 600;
		margin-bottom: 0.5rem;
	}

	.tier-price {
		font-size: 1.5rem;
		font-weight: 700;
		color: var(--primary);
	}

	.tier-features {
		margin-bottom: 1.5rem;
	}

	.tier-feature {
		margin-bottom: 0.5rem;
		display: flex;
		align-items: baseline;
	}

	.current-subscription {
		margin-top: 2rem;
		border-top: 1px solid var(--border-color);
		padding-top: 1.5rem;
	}

	/* Mobile styles */
	@media (max-width: 768px) {
		.desktop-nav {
			display: none;
		}

		.mobile-nav {
			display: flex;
			position: fixed;
			bottom: 0;
			left: 0;
			width: 100%;
			background: var(--dark-card);
			padding: 0.8rem 0;
			justify-content: space-around;
			box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
			z-index: 100;
		}

		.mobile-nav-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-decoration: none; /* Added for <a> tags */
			color: var(--text-light); /* Added for <a> tags */
			font-weight: 500; /* Added for <a> tags */
			transition: color 0.2s; /* Added for <a> tags */
			cursor: pointer; /* Added for <a> tags */
			font-size: 0.75rem;
		}
		.mobile-nav-item:hover { /* Added for <a> tags */
			color: var(--primary);
		}

		.mobile-nav-item.active {
			color: var(--primary);
		}

		.icon {
			width: 1.5rem;
			height: 1.5rem;
			margin-bottom: 0.3rem;
		}

		.container {
			padding-bottom: 70px;
		}

		.alert.has-mobile-nav {
			/* No longer needed with fixed positioning */
		}

		.flex-wrap {
			justify-content: center;
		}

		/* Tighten top nav spacing on mobile */
		.container {
			padding: 0.4rem 0.75rem 70px;
		}

		.desktop-nav {
			padding: 0.4rem 0;
			margin-bottom: 0.5rem;
			min-height: unset;
		}
	}

	@media (max-width: 576px) {
		:global(.card) {
			padding: 1.2rem;
		}

		.admin-section-header h3 {
			font-size: 1rem;
		}

		.product-item {
			flex-direction: column;
			align-items: flex-start;
			gap: 0.5rem;
		}

		.product-item button {
			width: 100%;
		}

		:global(.customer-card) {
			max-width: 100%;
		}
	}

	@media (max-width: 480px) {
		.customer-info {
			font-size: 0.85rem;
		}

		:global(.btn) {
			padding: 0.4rem 1rem;
			font-size: 0.9rem;
		}

		.progress-text {
			font-size: 0.7rem;
		}
	}

	.nav-logo {
		font-size: 1.5rem;
		font-weight: 700;
		color: var(--primary);
	}

	.nav-links {
		display: flex;
		gap: 1rem;
	}

	.nav-link {
		text-decoration: none;
		color: var(--text-light);
		font-weight: 500;
		transition: color 0.2s;
		cursor: pointer;
	}

	.nav-link:hover {
		color: var(--primary);
	}

	.nav-greeting {
		color: var(--primary);
		font-weight: 600;
		font-size: 0.95rem;
		padding-right: 0.5rem;
		border-right: 1px solid var(--border-color);
		margin-right: 0.25rem;
	}

	.mobile-nav-greeting {
		width: 100%;
		text-align: center;
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--primary);
		padding: 3px 0 2px;
		border-bottom: 1px solid var(--border-color);
	}

	.text-center {
		text-align: center;
	}

	.text-muted {
		color: var(--text-muted);
		font-size: 0.875rem;
	}

	/* Utility classes */
	:global(.mt-1) {
		margin-top: 0.25rem;
	}

	:global(.mt-2) {
		margin-top: 0.5rem;
	}

	:global(.mt-3) {
		margin-top: 1rem;
	}

	:global(.mt-4) {
		margin-top: 1.5rem;
	}

	:global(.mt-5) {
		margin-top: 2rem;
	}

	:global(.mb-1) {
		margin-bottom: 0.25rem;
	}

	:global(.mb-2) {
		margin-bottom: 0.5rem;
	}

	:global(.mb-3) {
		margin-bottom: 1rem;
	}

	:global(.mb-4) {
		margin-bottom: 1.5rem;
	}

	:global(.mb-5) {
		margin-bottom: 2rem;
	}

	:global(.mr-4) {
		margin-right: 1.5rem;
	}

	:global(.flex) {
		display: flex;
	}

	:global(.flex-col) {
		flex-direction: column;
	}

	:global(.items-center) {
		align-items: center;
	}

	:global(.justify-between) {
		justify-content: space-between;
	}

	:global(.justify-center) {
		justify-content: center;
	}

	:global(.gap-2) {
		gap: 0.5rem;
	}

	:global(.gap-3) {
		gap: 1rem;
	}

	:global(.flex-grow) {
		flex-grow: 1;
	}

	:global(.w-full) {
		width: 100%;
	}

	:global(.max-w-md) {
		max-width: 28rem;
	}

	:global(.mx-auto) {
		margin-left: auto;
		margin-right: auto;
	}

	:global(.alert-container) {
		position: fixed;
		top: 1rem;
		left: 50%;
		transform: translateX(-50%);
		z-index: 9999;
		width: 90%;
		max-width: 600px;
		pointer-events: none;
	}

	:global(.alert) {
		padding: 0.875rem 1.25rem;
		border-radius: var(--border-radius-sm);
		position: relative;
		width: 100%;
		z-index: 10000;
		animation: slideDown 0.3s ease-in-out;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
		pointer-events: auto;
		text-align: center;
		font-weight: 500;
		margin-bottom: 0.5rem;
	}

	@media (max-width: 768px) {
		:global(.alert-container) {
			width: 95%;
			top: 0.5rem;
		}

		:global(.alert) {
			padding: 0.625rem 1rem;
			font-size: 0.9rem;
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}

		to {
			opacity: 1;
		}
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	:global(.alert-success) {
		background-color: #d4edda;
		color: #155724;
	}

	:global(.alert-danger) {
		background-color: #f8d7da;
		color: #721c24;
	}

	:global(.alert-info) {
		background-color: #d1ecf1;
		color: #0c5460;
	}

	:global(.alert-warning) {
		background-color: #fff3cd;
		color: #856404;
	}

	.customer-modal {
		max-height: 90vh;
		overflow-y: auto;
	}

	.form-group.compact {
		margin-bottom: 0.75rem;
	}
	fieldset.form-group.compact { /* Specificity for fieldset */
		border: none; /* Remove default fieldset border */
		padding: 0; /* Remove default fieldset padding */
	}


	.contact-options {
		display: flex;
		gap: 1.5rem;
	}

	.contact-option {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.modal-body {
		padding-bottom: 1rem;
	}

	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 50;
		/* Allow backdrop itself to scroll on small screens */
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}

	.modal {
		background-color: var(--dark-card);
		border-radius: var(--border-radius-sm);
		padding: 1.5rem;
		width: 90%;
		max-width: 500px;
		max-height: 90vh;
		overflow-y: auto;
	}

	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1rem;
	}

	.modal-title {
		font-size: 1.25rem;
		font-weight: 600;
	}

	.modal-close {
		background: none;
		border: none;
		cursor: pointer;
		font-size: 1.5rem;
		color: currentColor;
	}

	/* Tabs */
	.tabs {
		display: flex;
		border-bottom: 1px solid var(--border-color);
		margin-bottom: 1.5rem;
	}

	.tab {
		padding: 0.75rem 1rem;
		cursor: pointer;
		border-bottom: 2px solid transparent;
		transition: all 0.2s;
	}

	.tab.active {
		border-bottom-color: var(--primary);
		color: var(--primary);
		font-weight: 500;
	}
	.payment-status-unknown .payment-status-icon,
	.payment-status-error .payment-status-icon {
		background-color: #A0AEC0;
		color: white;
	}

	/* Employee Management Styles */
	.employee-list {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.employee-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1rem;
		border-radius: var(--border-radius-sm);
		background-color: var(--bg-darker);
		border: 1px solid var(--border-color);
	}

	.employee-details {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		flex: 1;
	}

	.employee-name {
		font-weight: 600;
		color: var(--text-light);
		font-size: 1rem;
	}

	.employee-email {
		color: var(--text-muted);
		font-size: 0.875rem;
	}

	.employee-permissions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.25rem;
		margin-top: 0.5rem;
	}

	.permission-badge {
		display: inline-block;
		padding: 0.125rem 0.5rem;
		border-radius: var(--border-radius);
		font-size: 0.75rem;
		font-weight: 500;
		background-color: var(--primary);
		color: white;
	}

	.permission-badge.full-access {
		background-color: var(--success);
	}

	.employee-actions {
		display: flex;
		gap: 0.5rem;
		align-items: center;
	}

	/* Branding toggle row */
	.branding-toggle-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 1rem;
	}

	/* iOS-style toggle switch */
	.toggle-switch {
		position: relative;
		display: inline-block;
		width: 52px;
		height: 28px;
		flex-shrink: 0;
		cursor: pointer;
	}

	.toggle-switch input {
		opacity: 0;
		width: 0;
		height: 0;
		position: absolute;
	}

	.toggle-slider {
		position: absolute;
		inset: 0;
		background-color: var(--border-color);
		border-radius: 28px;
		transition: background-color 0.2s;
	}

	.toggle-slider::before {
		content: '';
		position: absolute;
		width: 22px;
		height: 22px;
		left: 3px;
		top: 3px;
		background-color: white;
		border-radius: 50%;
		transition: transform 0.2s;
		box-shadow: 0 1px 3px rgba(0,0,0,0.3);
	}

	.toggle-switch input:checked + .toggle-slider {
		background-color: var(--primary);
	}

	.toggle-switch input:checked + .toggle-slider::before {
		transform: translateX(24px);
	}

	.employee-modal {
		max-width: 600px;
		max-height: 90vh;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}

	.permissions-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 0.75rem;
		margin-top: 0.5rem;
	}

	.permission-option {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		cursor: pointer;
		padding: 0.5rem;
		border-radius: var(--border-radius-sm);
		transition: background-color 0.2s;
	}

	.permission-option:hover {
		background-color: rgba(255, 255, 255, 0.05);
	}

	.permission-label {
		color: var(--text-light);
		font-weight: 500;
	}

	.full-access-label {
		color: var(--success);
		font-weight: 600;
	}

	.permission-option input[type="checkbox"]:disabled + .permission-label {
		color: var(--text-muted);
		opacity: 0.6;
	}

	@media (max-width: 768px) {
		.employee-item {
			flex-direction: column;
			align-items: flex-start;
			gap: 0.75rem;
		}

		.employee-actions {
			width: 100%;
			justify-content: flex-end;
		}

		/* On mobile: let the backdrop scroll, start modal at top */
		.modal-backdrop {
			align-items: flex-start;
			padding: 1rem 0;
		}

		.employee-modal {
			max-width: 95%;
			max-height: none;       /* Remove the vh cap — backdrop scrolls instead */
			margin: 0 auto;
			overflow-y: visible;    /* Content flows naturally, backdrop is the scroller */
		}

		/* Other modals keep a sensible max-height but allow backdrop scroll */
		.modal:not(.employee-modal) {
			max-height: none;
			margin: 0 auto;
		}
	}

	/* Store Branding Styles - Updated Logo Display */
	.nav-logo-container {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.desktop-nav {
		position: relative;
		min-height: 70px;
	}

	.desktop-nav.has-logo {
		min-height: 140px;
	}

	.store-logo-display {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.store-logo-circle {
		width: 100px;
		height: 100px;
		border-radius: 50%;
		border: 3px solid var(--primary, #30d5c8);
		background-color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		padding: 8px;
	}

	.store-logo-centered {
		max-width: 80%;
		max-height: 80%;
		object-fit: contain;
	}

	:global(body.light-mode .store-logo-circle) {
		border-color: #30d5c8;
		background-color: white;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	}

	/* Logo preview in admin */
	.logo-preview-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.75rem;
		padding: 1.5rem;
		background-color: var(--bg-darker);
		border-radius: var(--border-radius-sm);
		border: 1px solid var(--border-color);
	}

	.logo-preview-circle {
		width: 150px;
		height: 150px;
		border-radius: 50%;
		border: 3px solid var(--primary, #30d5c8);
		background-color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		padding: 12px;
	}

	.logo-preview-img {
		max-width: 80%;
		max-height: 80%;
		object-fit: contain;
	}

	.theme-options {
		display: flex;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.theme-option {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.5rem;
		padding: 1rem;
		border: 2px solid var(--border-color);
		border-radius: var(--border-radius-sm);
		cursor: pointer;
		transition: all 0.2s;
		min-width: 120px;
	}

	.theme-option:hover {
		border-color: var(--primary);
	}

	.theme-option.selected {
		border-color: var(--primary);
		background-color: rgba(48, 213, 200, 0.1);
	}

	.theme-option input[type="radio"] {
		display: none;
	}

	.theme-preview {
		width: 60px;
		height: 40px;
		border-radius: var(--border-radius-sm);
		border: 1px solid var(--border-color);
	}

	.dark-preview {
		background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
	}

	.light-preview {
		background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
		border: 1px solid #cbd5e0;
	}

	.logo-preview-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.75rem;
		padding: 1rem;
		background-color: var(--bg-darker);
		border-radius: var(--border-radius-sm);
		border: 1px solid var(--border-color);
	}

	.logo-preview {
		max-width: 200px;
		max-height: 100px;
		object-fit: contain;
	}

	/* Light Mode Styles - Comprehensive */
	:global(body.light-mode) {
		--bg-dark: #f7fafc;
		--bg-darker: #edf2f7;
		--text-light: #2d3748;
		--text-muted: #718096;
		--border-color: #e2e8f0;
		background-color: #f7fafc;
		color: #2d3748;
	}

	:global(body.light-mode .container) {
		background-color: #f7fafc;
	}

	:global(body.light-mode .card.dark-card) {
		background-color: white;
		color: #2d3748;
		border: 1px solid #e2e8f0;
		box-shadow: 0 1px 3px rgba(0,0,0,0.1);
	}

	:global(body.light-mode .form-input.dark-input) {
		background-color: #f7fafc;
		color: #2d3748;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .form-input.dark-input::placeholder) {
		color: #a0aec0;
	}

	:global(body.light-mode .nav) {
		background-color: white;
		border-bottom: 1px solid #e2e8f0;
	}

	:global(body.light-mode .nav-logo) {
		color: #2d3748;
	}

	:global(body.light-mode .nav-link) {
		color: #718096;
	}

	:global(body.light-mode .nav-link:hover) {
		color: #30d5c8;
	}

	:global(body.light-mode .mobile-nav) {
		background-color: white;
		border-top: 1px solid #e2e8f0;
	}

	:global(body.light-mode .mobile-nav-item) {
		color: #718096;
	}

	:global(body.light-mode .admin-card) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode .admin-card h4) {
		color: #2d3748;
	}

	:global(body.light-mode .admin-section-header) {
		background-color: white;
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode .admin-section-header h3) {
		color: #2d3748;
	}

	:global(body.light-mode .admin-section-content) {
		background-color: white;
		color: #2d3748;
	}

	:global(body.light-mode .customer-card) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode .customer-card h4) {
		color: #2d3748;
	}

	:global(body.light-mode .customer-card small) {
		color: #718096;
	}

	:global(body.light-mode .history-table th) {
		background-color: #edf2f7;
		color: #2d3748;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .history-table td) {
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode .history-table tr:hover) {
		background-color: #f7fafc;
	}

	:global(body.light-mode .modal.dark-modal) {
		background-color: white;
		color: #2d3748;
		border: 1px solid #e2e8f0;
	}

	:global(body.light-mode .modal-header) {
		border-color: #e2e8f0;
	}

	:global(body.light-mode .modal-title) {
		color: #2d3748;
	}

	:global(body.light-mode .employee-item) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .employee-name) {
		color: #2d3748;
	}

	:global(body.light-mode .permission-option:hover) {
		background-color: rgba(0, 0, 0, 0.05);
	}

	:global(body.light-mode .product-item) {
		background-color: #edf2f7;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .search-input) {
		background-color: white;
		color: #2d3748;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .progress-container) {
		background-color: #e2e8f0;
	}

	:global(body.light-mode .logo-preview-container) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .theme-option) {
		border-color: #e2e8f0;
		background-color: white;
	}

	:global(body.light-mode .theme-option.selected) {
		border-color: #30d5c8;
		background-color: rgba(48, 213, 200, 0.1);
	}

	:global(body.light-mode .redeem-product-item) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .redeem-product-item.selected) {
		border-color: #30d5c8;
		background-color: rgba(48, 213, 200, 0.1);
	}

	:global(body.light-mode .redeem-product-item.disabled) {
		background-color: #edf2f7;
		opacity: 0.6;
	}

	:global(body.light-mode .contact-option) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode small) {
		color: #718096;
	}

	:global(body.light-mode .text-gray-400) {
		color: #718096;
	}

	:global(body.light-mode .payment-status-box) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .feature-locked-message) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode .email-preview-content) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
		color: #2d3748;
	}

	:global(body.light-mode .permissions-list) {
		background-color: #f7fafc;
		border-color: #e2e8f0;
	}

	:global(body.light-mode .alert) {
		border-color: #e2e8f0;
	}

	:global(body.light-mode .alert-success) {
		background-color: #c6f6d5;
		color: #22543d;
	}

	:global(body.light-mode .alert-danger) {
		background-color: #fed7d7;
		color: #742a2a;
	}

	:global(body.light-mode .alert-warning) {
		background-color: #fefcbf;
		color: #744210;
	}

	:global(body.light-mode .alert-info) {
		background-color: #bee3f8;
		color: #2a4365;
	}

	/* Color Picker Styles */
	.color-picker-row {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		flex-wrap: wrap;
	}

	.color-preview {
		width: 40px;
		height: 40px;
		border-radius: var(--border-radius-sm);
		border: 2px solid var(--border-color);
		cursor: pointer;
		transition: transform 0.2s, border-color 0.2s;
		flex-shrink: 0;
	}

	.color-preview:hover {
		transform: scale(1.1);
		border-color: var(--primary);
	}

	.color-input {
		width: 100px;
		flex-shrink: 0;
	}

	.color-picker-modal {
		max-width: 400px;
		width: 90%;
	}

	.color-palette {
		display: grid;
		grid-template-columns: repeat(8, 1fr);
		gap: 0.5rem;
		margin: 1rem 0;
	}

	.color-swatch {
		width: 100%;
		aspect-ratio: 1;
		border: 2px solid transparent;
		border-radius: var(--border-radius-sm);
		cursor: pointer;
		transition: transform 0.2s, border-color 0.2s;
		padding: 0;
	}

	.color-swatch:hover {
		transform: scale(1.1);
		border-color: white;
		z-index: 1;
	}

	.color-input-row {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	@media (max-width: 480px) {
		.color-palette {
			grid-template-columns: repeat(6, 1fr);
			gap: 0.35rem;
		}

		.color-picker-row {
			gap: 0.5rem;
		}

		.color-preview {
			width: 35px;
			height: 35px;
		}

		.color-input {
			width: 80px;
		}
	}

	/* Remove light mode CSS since we're scrapping it */
	/* Keeping only essential styles for default dark mode */

	/* Premium Logo in Navigation */
	.nav-logo-premium {
		display: flex;
		align-items: center;
		justify-content: center;
		max-height: 50px;
		max-width: 150px;
	}

	.nav-logo-premium img {
		max-height: 50px;
		max-width: 150px;
		object-fit: contain;
	}

	/* Footer Styles */
	.app-footer {
		margin-top: 2rem;
		padding: 1rem 0;
		border-top: 1px solid var(--border-color);
		text-align: center;
	}

	.footer-content {
		color: var(--text-muted);
		font-size: 0.875rem;
	}

	.footer-branding strong {
		color: var(--primary);
	}

	@media (max-width: 768px) {
		.app-footer {
			padding-bottom: 80px; /* Account for mobile nav */
		}
	}

	@media (max-width: 768px) {
		.store-logo-nav {
			max-height: 60px;
			max-width: 150px;
		}
	}
</style>
